import {
    EzObject,
    EzBoolean,
    EzNumber,
    EzString,
    EzArray,
    EzFunction,
    EzJson,
    EzPromise,
    EzHtml,
    EzUrl
} from '/ezlibrary/helpers/EzHelpers.js';

import {
    EzException,
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzRegistrationState,
    EzInstanceState
} from '/ezlibrary/enums/EzEnumerations.js';

import { EzLogger } from '/public/javascript/common/ezclocker-logger.js';

import '/ezlibrary/ez-event-engine.js';

// TODO: Remove EzEnumBuilder import once all use is migrated
import '/ezlibrary/EzEnumBuilder.js';

// TODO: Remove EzGetterSetter import once all use is migrated
import { EzGetterSetter } from '/ezlibrary/ez-getter-setter.js';

// TODO: Remove EzGetter import once all use is migrated
import { EzGetter } from '/ezlibrary/ez-getter.js';

import { EzCustomEvent } from '/ezlibrary/events/EzCustomEvent.js';


/**
 * @class
 * @description
 * Provides utility methods, helper methods, and ezClocker API registration/access.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * ENGINEERING NOTES
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * You do not need to import EzApi if the HTML page includes the following script tag:
 *      <script src="/ezwp/js/ez-core.bundle.js?v=r75-5"></script>
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *      import { ezApi } from '/public/javascript/common/ezapi.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * EzApi singleton instance ready when true:
 *     globalThis?.ezApi?.ready && globalThis.ezApi?.ezclocker;
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Listen for singleton instance ready:
 * 1) Listening class is extending EzClass:
 *     EzClass.ezWaitReady(
 *          {listening_class_extending_EzClass}.ezOnEzApiReadyEventName,
 *          {listening_class_extending_EzClass}.#ezRegistrator);
 * 2) Listening class is NOT extending EzClass:
 *      document.addEventListener(
 *          {listening_class_NOT_extending_EzClass}.ezOnEzApiReadyEventName,
 *          {listening_class_NOT_extending_EzClass}.{call_back_method});
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Singleton instance:
 *      From outside this class: globalThis.ezApi
 *      From within this class
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzApi {
    /**
     * @static
     * @public @readonly @property
     * Gets Gets a constant string representing the name for event document.onreadystatechange
     *
     * @returns {string}
     */
    static get DOCUMENT_EVENT_NAME_onreadystatechange() {
        return 'onreadystatechange';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a constant string representing the name for event document.DOMContentLoaded
     *
     * @returns {string}
     */
    static get DOCUMENT_EVENT_NAME_DOMContentLoaded() {
        return 'DOMContentLoaded';
    }

    /**
     * @stratic
     * @public @readonly @property
     * Gets a constant string representing the name of the event name property that is defined by classes in their
     * ezEventNamesstatic property that is dispatched after the class is registered with ezApi.
    */
    static get API_READY_EVENT_NAME_PROPERTY() {
        return 'onReady';
    }

    /**
     * @public @method @property
     * Gets the type name of the EzApi class
     */
    get ezTypeName() {
        return 'EzApi';
    }

    /**
     * @public @method @property
     *
     * @type {object}
     *
     * @deprecated
     * Stop all use, will remove in future release.
     */
    #paramEvalClass = {
        functionClass: 'EzApi'
    };
    /**
     * @public @readonly @property
     * Gets
     *
     * @returns {object}
     */
    get paramEvalClass() {
        return this.#paramEvalClass;
    }

    /**
     * @private @field
     * Stores the offical map of registered singleton instances (classes and enumerations)
     *
     * @type {object}
     */
    #ezclocker = {};
    /**
     * @public @readonly @property
     * Gets the offical map of registered singleton instances (classes and enumerations)
     *
     * @returns {object}
     */
    get ezclocker() {
        return this.#ezclocker;
    }
    /**
     * @public @readonly @property
     * An alias to property globalThis.ezApi.ezclocker
     *
     * @returns {object}
     */
    get ezClocker() {
        return this.#ezclocker;
    }

    /**
     * @private @field
     * Stores the ezDebugMode setting
     * Default: false
     *
     * @type {bopolean}
     */
    #ezDebugMode = false;
    /**
     * @public @property @getter
     * Gets
     *
     * @returns {boolean}
     */
    get ezDebugMode() {
        return this.#ezDebugMode;
    }
    /**
     * @public @property @setter
     * Sets
     *
     * @param {boolean} debugMode
     * Default: false
     */
    set ezDebugMode(debugMode = false) {
        this.#ezDebugMode = EzBoolean.booleanOrFalse(debugMode);

        for (const singletonEzApiName in this.ezclocker) {
            if (this.ezclocker.hasOwnProperty(singletonEzApiName)) {
                this.ezclocker[singletonEzApiName].ezDebugMode = this.#ezDebugMode;
            }
        }
    }

    /**
     * @public @method @property
     * Migrate away from using EzApi.ezInstance.enums
     * Instead, import the enumeration class and reference the class name.
     * @deprecated
     * Most numerations no longer register here. See EzEnumeration2.js and EzEnumerations.js
     */
    #enums = [];
    /**
     * @public @readonly @property
     * Gets all registered event names (access with: EzApi.ezInstance.events[{eventOwner}.{eventCategory}])
     *
     * @returns {array}
     *
     * @deprecated
     * Stop all use, will remove in future release.
     * Migrate to EzEventEngine functionality
     */
    get enums() {
        return this.#enums;
    }

    /**
     * @private @field
     * All registered event names (access with: EzApi.ezInstance.events[{eventOwner}.{eventCategory}])
     *
     * @deprecated
     * Stop all use, will remove in future release.
     * Migrate to EzEventEngine functionality
     *
     * @type {array}
     */
    #events = [];
    /**
     * @public @readonly @property
     * Gets all registered event names (access with: EzApi.ezInstance.events[{eventOwner}.{eventCategory}])
     *
     * @returns {array}
     *
     * @deprecated
     * Stop all use, will remove in future release.
     * Migrate to EzEventEngine functionality
     */
    get events() {
        return this.#events;
    }

    /**
     * @public @method @property
     * EzApiRegistrationScope is deprecated and should no longer be used anywhere.
     *
     * @deprecated
     * Stop all use, will remove in future release.
     */
    get EzApiRegistrationScope() {
        return {
            EZCLOCKER: 'ezclocker',
            ROOT: 'r',
            PUBLIC: 'p',
            SECURE: 's',
            EXTERNAL: 'x',
            DIALOGS: 'd',
            WINDOW: 'window',
            fromEzApiRegistrationScopeValue: (ezApiRegistrationScopeValue) => {
                for (const key in EzApi.ezInstance.EzApiRegistrationScope) {
                    if (EzApi.ezInstance.EzApiRegistrationScope[key] === ezApiRegistrationScopeValue) {
                        return EzApi.ezInstance.EzApiRegistrationScope;
                    }
                }

                return EzApi.ezInstance.EzApiRegistrationScope.ROOT;
            }
        };
    }

    /**
     * @public @readonly @property
     * Gets
     *
     * @returns {object}
     *
     * @deprecated
     * Use EzApi.ezInstance.ezclocker instead
     */
    get Public() {
        return this.ezclocker;
    }

    /**
     * @public @readonly @property
     * Gets
     *
     * @returns {object}
     *
     * @deprecated
     * Use EzApi.ezInstance.ezclocker instead
     */
    get public() {
        return this.ezclocker;
    }

    /**
     * @public @readonly @property
     * Gets
     *
     * @returns {object}
     *
     * @deprecated
     * Use EzApi.ezInstance.ezclocker instead
     */
    get p() {
        return this.ezclocker;
    }

    /**
     * @public @readonly @property
     * Gets
     *
     * @returns {object}
     *
     * @deprecated
     * Use EzApi.ezInstance.ezclocker instead
     */
    get Secure() {
        return this.ezclocker;
    }

    /**
     * @public @readonly @property
     * Gets
     *
     * @returns {object}
     *
     * @deprecated
     * Use EzApi.ezInstance.ezclocker instead
     */
    get secure() {
        return this.ezclocker;
    }

    /**
     * @public @readonly @property
     * Gets
     *
     * @returns {object}
     *
     * @deprecated
     * Use EzApi.ezInstance.ezclocker instead
     */
    get s() {
        return this.ezclocker;
    }

    /**
     * @public @readonly @property
     * Gets
     *
     * @returns {object}
     *
     * @deprecated
     * Use EzApi.ezInstance.ezclocker instead
     */
    get External() {
        return this.ezclocker;
    }

    /**
     * @public @readonly @property
     * Gets
     *
     * @returns {object}
     *
     * @deprecated
     * Use EzApi.ezInstance.ezclocker instead
     */
    get external() {
        return this.ezclocker;
    }

    /**
     * @public @readonly @property
     * Gets
     *
     * @returns {object}
     *
     * @deprecated
     * Use EzApi.ezInstance.ezclocker instead
     */
    get x() {
        return this.ezclocker;
    }

    /**
     * @public @readonly @property
     * Gets
     *
     * @returns {object}
     *
     * @deprecated
     * Use EzApi.ezInstance.ezclocker instead
     */
    get Dialogs() {
        return this.ezclocker;
    }

    /**
     * @deprecated
     * Use EzApi.ezInstance.ezclocker instead
     * Will remove in future release.
     * @public @method @property
     */
    get dialogs() {
        return this.ezclocker;
    }

    /**
     * @deprecated
     * Use EzApi.ezInstance.ezclocker instead
     * Will remove in future release.
     * @public @method @property
     */
    get d() {
        return this.ezclocker;
    }

    /**
     * @public @property @getter
     * Gets
     *
     * @returns {boolean}
     */
    get enableDebugLogging() {
        return EzBoolean.booleanOrFalse(EzLogger?.ezDebugEnabled);
    }

    /**
     * @public @property @getter
     * Gets
     *
     * @returns {boolean}
     */
    get enableInfoLogging() {
        return EzBoolean.booleanOrFalse(EzLogger?.ezInfoEnabled);
    }

    /**
     * @public @property @getter
     * Gets
     *
     * @returns {boolean}
     */
    get enableWarnLogging() {
        return EzBoolean.booleanOrFalse(EzLogger?.ezWarnEnabled);
    }

    /**
     * @public @property @getter
     * Gets
     *
     * @returns {boolean}
     */
    get enableErrorLogging() {
        return EzBoolean.booleanOrFalse(EzLogger?.ezErrorEnabled);
    }

    /**
     * @protected @method
     * Initalizes ezApi
     *
     * @returns {EzApi}
     */
    ezInit() {
        EzApi.ezInstance.ezStates.push('INITIALIZED');

        EzApi.ezInstance.ready = true;

        return EzApi.ezInstance;
    }

    /**
     * @public @method
     * Creates a new Error() with the provided message.
     * @param {string} em
     */
    ezException(em) {
        return new Error(em);
    }

    /**
     * @deprecated
     * Migrate to creating new EzBadParamException instead of calling ezApi.ezBadParm().
        Example: throw new EzBadParamException(paramName, className, functionName);
     * @public @method
     * Creates a new Error() with the provided message.
     * @param {string} paramName
     * @param {string} className
     * @param {string} functionName
     * @returns {EzBadParamException}
     */
    ezBadParam(paramName, className, functionName) {
        return new EzBadParamException(paramName, className, functionName);
    }

    /**
     * @public @method
        Obtains the api reference for the provided api id. If the provided api id does not match a known api reference,
        then the defaultApiReference is returned.
     * @param {string} apiId
     * @param {null|object} defaultApiReference
     * @returns {object}
     */
    ezGetApiIdReference(apiId, defaultApiReference) {
        return EzString.hasLength(apiId) &&
            EzObject.isValid(EzApi.ezInstance.ezclocker[apiId])
            ? EzApi.ezInstance.ezclocker[apiId]
            : defaultApiReference;
    }

    /**
     * @public @method
     * Determines if the window.console[{FunctionName}] is available to call.
     * @param {string} functionName
     * @returns {boolean}
     */
    ezIsWindowConsoleFunctionAvailable(functionName) {
        return EzObject.isValid(window.console) &&
            EzFunction.isFunction(window.console[functionName]);
    }

    /**
     * @public @method
        Sets the enableDebugLogging. When true, ezApi will allow log writing to the window.console.debug.
        If debugLogEnabled is null, then it is assumed false.
     * @param {null|Boolean} debugLogEnabled
     */
    ezSetEnableDebugLogging(debugLogEnabled) {
        EzApi.ezInstance.enableDebugLogging = EzBoolean.isTrue(debugLogEnabled);
    }

    /**
     * @public @method
        Sets the enableInfoLogging. When true, ezApi will allow log writing to the window.console.info.
        If infoLogEnabled is null, then it is assumed false.
     * @param {null|Boolean} infoLogEnabled
     */
    ezSetEnableInfoLogging(infoLogEnabled) {
        EzApi.ezInstance.enableInfoLogging = EzBoolean.isTrue(infoLogEnabled);
    }

    /**
     * @public @method
        Sets the enableErrorLogging. When true, ezApi will allow log writing to the window.console.warn.
        If warnLogEnabled is null, then it is assumed false.
     * @param {null|Boolean} warnLogEnabled
     */
    ezSetEnableWarnLogging(warnLogEnabled) {
        EzApi.ezInstance.enableWarnLogging = EzBoolean.isTrue(warnLogEnabled);
    }

    /**
     * @public @method
        Sets the enableErrorLogging. When true, ezApi will allow log writing to the window.console.error.
        If infoLogEnabled is null, then it is assumed false.
     * @param {null|boolean} errorLogEnabled
     */
    ezSetEnableErrorLogging(errorLogEnabled) {
        EzApi.ezInstance.enableErrorLogging = EzBoolean.isTrue(errorLogEnabled);
    }

    /**
     * @public @method
     * Logs a function parameter error if the evalResult is false.
     * @param {Boolean|null} evalResult
     * @param {string|null} paramName
     * @param {string|null} functionName
     * @param {string|null} className
     * @returns {boolean}
     * Returns true if an error log was NOT written, true otherwise.
     */
    ezValidateParam(evalResult, paramName, functionName, className) {
        return EzApi.ezInstance.ezValidParam({
            result: EzBoolean.isTrue(evalResult),
            param: EzString.stringOrEmpty(paramName),
            callerFunction: EzString.stringOrEmpty(functionName),
            functionClass: EzString.stringOrEmpty(className)
        });
    }

    /**
     * @public @method
     * Logs a function parameter error if the evalResult is false.
     * @param {Boolean|null} evalResult
     * @param {string|null} paramName
     * @param {string|null} functionName
     * @param {string|null} className
     * @returns {boolean}
     * Returns true if an error log was written, false otherwise.
     */
    ezParamNotValid(evalResult, paramName, functionName, className) {
        return EzApi.ezInstance.ezNotValidParam({
            result: EzBoolean.isTrue(evalResult),
            param: EzString.stringOrEmpty(paramName),
            callerFunction: EzString.stringOrEmpty(functionName),
            functionClass: EzString.stringOrEmpty(className)
        });
    }

    /**
     * @public @method
     * Processes the evalObject to determine if a bad parameter error log needs written.
     * @param {object} evalObject
     * @returns {boolean}
     * Returns true if an error log was NOT written. False otherwise.
     */
    ezValidParam(evalObject) {
        return !EzApi.ezInstance.ezNotValidParam(evalObject);
    }

    /**
     * @public @method
     * Processes the evalObject to determine if a function parameter error needs logged and returns if an error
     * was logged or not (indicating the param is not valid).
     * @param {object} evalObject
     * @returns {boolean}
     * Returns true if an error log was written. False otherwise.
     */
    ezNotValidParam(evalObject) {
        if (!EzObject.isValid(evalObject)) {
            EzApi.ezInstance.ezLogError('The evalObject param is required in call to EzApi.ezValidParam().');

            return true;
        }

        if (EzBoolean.isFalse(evalObject)) {
            let em;

            em = EzString.hasLength(evalObject.param)
                ? `Then ${evalObject.param} is required`
                : 'Failed to validate a required param';

            if (EzString.hasLength(evalObject.functionClass) && EzString.hasLength(evalObject.callerFunction)) {
                em = `${em} in call to ${evalObject.functionClass}. ${evalObject.callerFunction}().`;
            } else if (EzString.hasLength(evalObject.callerFunction)) {
                em = `${em} in call to ${evalObject.callerFunction}().`;
            }

            EzLogger.error(em);

            return true;
        }

        return false;
    }

    /**
     * @public @method
        Writes to the window.console.debug if available AND EzApi.ezInstance.enableDebugLogging is true.
     * @param {string} dMessage
     */
    ezLogDebug(dMessage) {
        if (EzApi.ezInstance.ezIsFalse(EzApi.ezInstance.ezDebugMode)) {
            return;
        }

        if (EzApi.ezInstance.ezIsWindowConsoleFunctionAvailable('debug') &&
            EzString.hasLength(dMessage) &&
            EzBoolean.isTrue(EzApi.ezInstance.enableDebugLogging)) {
            window.console.debug(EzString.em`[EzApi] [DEBUG]: ${dMessage}`);
        }
    }
    /**
     * @deprecated Migrate to: EzApi.ezInstance.ezLogInfo(iMessage)
     * @param {string} iMessage
     */
    ezLog(iMessage) {
        EzApi.ezInstance.ezLogInfo(iMessage);
    }
    /**
     * @public @method
     * Logs to window.console.info if available AND EzApi.ezInstance.enableInfoLogging is true.
     * @param {string} iMessage
     */
    ezLogInfo(iMessage) {
        if (EzApi.ezInstance.ezIsWindowConsoleFunctionAvailable('info') &&
            EzString.hasLength(iMessage) &&
            EzBoolean.isTrue(EzApi.ezInstance.enableInfoLogging)) {
            window.console.info('[EzApi] [INFO ]: ' + iMessage);
        }
    }
    /**
     * @public @method
     * Logs to window.console.warn if available AND EzApi.ezInstance.enableWarnLogging is true.
     * @param {string} message
     */
    ezLogError(wMessage) {
        if (EzApi.ezInstance.ezIsWindowConsoleFunctionAvailable('warn') &&
            EzString.hasLength(wMessage) &&
            EzBoolean.isTrue(EzApi.ezInstance.enableWarnLogging)) {
            window.console.warn('[EzApi] [ERROR]: ' + wMessage);
        }
    }

    /**
     * @public @method
     * Logs to window.console.warn if available AND EzApi.ezInstance.enableWarnLogging is true.
     * @param {string} message
     */
    ezLogWarn(eMessage) {
        if (EzApi.ezInstance.ezIsWindowConsoleFunctionAvailable('warn') &&
            EzString.hasLength(eMessage) &&
            EzBoolean.isTrue(EzApi.ezInstance.enableWarnLogging)) {
            window.console.warn('[EzApi] [WARN ]: ' + eMessage);
        }
    }
    /**
     * @deprecated Use EzApi.ezInstance.ezRegisterNewEnum() instead
     * @public @method
     * Registers an enumeration with the ezApi instance. Registered enumerations are available from
        EzApi.ezInstance.enums
     * @param {string} constName
     * @param {object} constInstance
     * @return {object}
        constInstance passed in
     */
    ezRegisterEnum(enumerationName, enumerationInstance) {
        if (!EzString.hasLength(enumerationName)) {
            throw new Error('[EzApi.ezRegisterEnum]: A non-empty name is required to register with ezApi.');
        }
        if (!EzObject.isValid(enumerationInstance)) {
            throw new Error('[EzApi.ezRegisterEnum]: You can only register valid, non-null instances with ezApi.');
        }

        EzApi.ezInstance.enums[enumerationName] = enumerationInstance;

        // Add the common properties and methods
        if (!EzString.hasLength(enumerationInstance.UNKNOWN)) {
            enumerationInstance.UNKNOWN = 'UNKNOWN';
        }
        if (!EzFunction.isFunction(enumerationInstance.ezFromValue)) {
            enumerationInstance.ezFromValue = (enumValue) => {
                return EzApi.ezInstance.ezGetJSONObjectPropertyValueOrDefault(enumValue, enumerationInstance.UNKNOWN);
            };
        }

        return EzApi.ezInstance.enums[enumerationName];
    }

    /**
     * @public @method
     * Registers a new enumeration instance. Calls new on the provided EnumerationReference.
     * Will add the UNKNOWN enum value if it is not already on the enumeration.
     * Will add the ezFromValue function if not already implemented on the provided enumeration instance.
     *   Example:
     *        function MyEnumeration() {
     *            ENUM_VALUE_1 = 'enumValue1';
     *            ENUM_EVALUE_2 = 'enumValue2';
     *        }
     *        document.addEventListener('onEzApiReady', function() { EzApi.ezInstance.ezRegisterNewEnum(MyEnumeration); });
     *
     * @param {Function} EnumerationReference
     * @param {string} enumName
     *
     * @returns {object}
     * Returns the registered enumeration instance
     *
     * @deprecated
     * Migrate to EzApi.ezInstance.ezRegisterEnumeration
     * Wil remove ezRegisterNewEnum in future release.
     */
    ezRegisterNewEnum(EnumerationReference, enumName) {
        if (!EzFunction.isFunction(EnumerationReference)) {
            throw new EzBadParamException('EnumerationReference', 'EzApi', 'ezRegisterNewEnum');
        }

        const enumInstance = new EnumerationReference();

        if (!EzString.hasLength(enumInstance.ENUM_NAME)) {
            if (EzString.hasLength(enumName)) {
                enumInstance.ENUM_NAME = enumName;
            } else if (EzObject.isValid(enumInstance.constructor.name)) {
                try {
                    enumInstance.ENUM_NAME = enumInstance.constructor.name;
                } catch (err) {
                    EzApi.ezInstance.ezLogError(
                        EzString.em`
                            EzApi.ezRegisterNewEnum: Unable to register a new enumeration.
                            Encounted an unexpected error during the attempt to discover the name of the enumeration
                            from {enumInstance}.constructor.name.
                            Error: ${EzString.stringWithLengthOrDefault(err?.message, '(no additional error message)')}`);

                    throw (err);
                }
            }
        }

        return EzApi.ezInstance.ezStoreEnum(enumInstance.ENUM_NAME, enumInstance);
    }

    /**
     * @public @method
     * Registers a new enumeration instance. Calls new on the provided EnumerationReference.
     *
     * @param {class} EnumerationClass
     *
     * @returns {object}
     *
     * @deprecated
     * Migrate to EzApi.ezInstance.ezRegisterEnumeration
     * Will remove EzApi.ezInstance._ezRegisterEzEnum in future release.
     */
    _ezRegisterEzEnum(EnumerationClass) {
        if (!EzObject.isValid(EnumerationClass)) {
            throw new EzBadParamException('EnumerationClass', 'EzApi', '_ezRegisterEzEnum');
        }

        // Add the static ezKeys property
        EnumerationClass.ezKeys = [];

        EnumerationClass.ezDisplayValues = {};

        // Constructor class and create the static ENUM values
        const enumInstance = new EnumerationClass();

        enumInstance.ezKeys = EnumerationClass.ezKeys;

        enumInstance.ezDisplayValues = EnumerationClass.ezDisplayValues;

        enumInstance.ezTypeName = EnumerationClass.name;

        // Add the static UNKNOWN property if not already available
        if (!EzObject.isValid(enumInstance.UNKNOWN)) {
            enumInstance.UNKNOWN = 'UNKNOWN';
        }

        // Replicate properties to class
        for (const prop in enumInstance) {
            // Only replicate non-underscore properties and functions
            if (EzObject.hasProperty(enumInstance, prop) && !EzObject.hasProperty(EnumerationClass, prop) && '_' !== prop.charAt(0)) {
                EnumerationClass[prop] = enumInstance[prop];
                if (prop === prop.toUpperCase() && EzApi.ezInstance.ezIsString(enumInstance[prop])) {
                    EnumerationClass.ezKeys.push(prop);

                    // Build display values object
                    if (!EzObject.hasProperty(enumInstance, `_${prop}`)) {
                        // Add internal detail property if none is available
                        enumInstance[`_${prop}`] = {
                            value: enumInstance[prop],
                            display: enumInstance[prop]
                        };
                    } else {
                        if (!EzObject.hasProperty(enumInstance[`_${prop}`], 'value')) {
                            enumInstance[`_${prop}`].value = prop;
                        }

                        if (!EzObject.hasProperty(enumInstance[`_${prop}`], 'display')) {
                            enumInstance[`_${prop}`].display = prop;
                        }

                        EnumerationClass.ezDisplayValues[`_${prop}`] = enumInstance[`_${prop}`].display;
                    }
                }
            }
        }

        // Add the generic ezTo enum value additional property function if not available
        if (!EzFunction.isFunction(enumInstance.ezTo)) {
            EnumerationClass.ezTo = (propName, enumValue) => {
                const self = EzApi.ezInstance.enums[EnumerationClass.name] || new EnumerationClass();

                if (!EzString.hasLength(enumValue) || -1 === self.ezKeys.indexOf(enumValue)) {
                    throw EzApi.ezInstance.ezException(EzString.em`
                    Enumeration value of ${enumValue} is not a member of enumeration ${self.ezTypeName}.`);
                }

                const _EnumValue = EzApi.ezInstance.ezSingleLineTemplate`_${enumValue}`;

                if (!EzObject.hasProperty(self, _EnumValue)) {
                    throw EzApi.ezInstance.ezException(EzString.em`
                        Enumeration ${self.ezTypeName} does not support additional properties for ${enumValue}`);
                }

                if (!EzObject.hasProperty(self[_EnumValue], propName)) {
                    throw EzApi.ezInstance.ezException(EzString.em`
                        Additional value property or ${propName} for enumeration value ${enumValue} on enumeration
                        ${self.ezTypeName} is not available.`);
                }

                return self[_EnumValue][propName];
            };

            enumInstance.ezTo = EnumerationClass.ezTo;
        }

        // Add the ezValueOf function if not on the EnumerationClass already
        if (!EzFunction.isFunction(enumInstance.ezValueOf)) {
            EnumerationClass.ezValueOf = (enumValue) => {
                const self = EzApi.ezInstance.enums[EnumerationClass.name] || new EnumerationClass();

                return EzString.hasLength(enumValue) && 0 > self.ezKeys.indexOf(enumValue.toUpperCase())
                    ? self[enumValue]
                    : self.UNKNOWN;
            };
            enumInstance.ezValueOf = EnumerationClass.ezValueOf;
        }

        // Add the ezToDisplayValue function if not already present
        if (!EzFunction.isFunction(enumInstance.ezToDisplayValue)) {
            EnumerationClass.ezToDisplayValue = (enumValue) => {
                const self = EzApi.ezInstance.enums[EnumerationClass.name] || new EnumerationClass();

                return self.ezTo('display', enumValue);
            };

            enumInstance.ezToDisplayValue = EnumerationClass.ezToDisplayValue;
        }

        EzApi.ezInstance.enums[EnumerationClass.name] = enumInstance;

        EzApi.ezInstance.ezLogDebug(`[EzApi: Registered API ${EnumerationClass.name}]`);

        return EzApi.ezInstance.enums[EnumerationClass.name];
    }

    /**
     * @deprecated
     * Migrate to EzApi.ezInstance.ezRegisterEnumeration
     * Wil remove EzApi.ezInstance.ezRegisterEzEnum in future release.
     * @public @method
     * Registers a EzEnum type. This registration and pattern replaces the older ezRegisterNewEnum functionality.
     * Define:
     * class EnumerationClass {
     *     constructor() {
     *          this._IgnoreProperty = 'This is not considered an enum value';
     *          this.ENUM_1 = 'ENUM_1';
     *          this.ENUM_2 = 'ENUM_2';
     *          this.ENUM_3 = 'ENUM_3';
     *          this.ENUM_4 = 'ENUM_4';
     *     }
     * }
     * document.addEventListener('onEzApiReady', () => EzApi.ezInstance.ezRegisterEzEnum(EnumerationClass));
     * Access:
     * Can access the enumeration's values staticly (once registered):
     *      let enum3 = EnumerationClass.ENUM_3;
     * Can also still access via EzApi.ezInstance.enums:
     *      let enum1 = EzApi.ezInstance.enums.EnumerationClass.ENUM_1;
     * New ezValueOf method always available (replaces the old ezFromValue method):
     *      let enum2 = EzApi.ezInstance.enums.EnumerationClass.ezValueOf('ENUM_2');
     * Access an array of the enumeration keys with property ezKeys:
     *      let enumKeysArray = EnumerationClass.ezKeys;
     */
    ezRegisterEzEnum(EnumerationClass) {
        if (!EzObject.isValid(EnumerationClass)) {
            throw new EzBadParamException('EnumerationClass', 'EzApi', 'ezRegisterEzEnum');
        }

        EzApi.ezInstance.enums[EnumerationClass.name] =
            EzApi.ezInstance.ezclocker.ezEnumBuilder.ezCreateEnumeration(EnumerationClass);

        if (EzObject.hasProperty(EzApi.ezInstance.enums[EnumerationClass.name], 'ezEventNames') &&
            EzObject.hasProperty(EzApi.ezInstance.enums[EnumerationClass.name]['ezEventNames'], EzApi.API_READY_EVENT_NAME_PROPERTY) &&
            EzString.hasLength(EzApi.ezInstance.enums[EnumerationClass.name]['ezEventNames'][EzApi.API_READY_EVENT_NAME_PROPERTY])) {
            // If the instance has defined a value fo ezOnReadyEventName, then
            // fire the event via the document.
            document.dispatchEvent(
                new CustomEvent(
                    EzApi.ezInstance.enums[EnumerationClass.name]['ezEventNames'][EzApi.API_READY_EVENT_NAME_PROPERTY],
                    {
                        bubbles: true,
                        apiClass: EnumerationClass,
                        apiName: EnumerationClass.name,
                        registeredInstance: EzApi.ezInstance.enums[EnumerationClass.name],
                        ezApi: EzApi.ezInstance
                    }));
        }

        return EzApi.ezInstance.enums[EnumerationClass.name];
    }

    /**
     * TODO: Remove this method once all use is migrated.
     * @public @method
     * New enumeration registration method
     *
     * @param {class} enumerationClazz
     *
     * @returns {EnumerationClazz}
     * Registered instance of enumeration class
     *
     * @deprecated
     * Migrate all enumerations to extend from EzEnumeration2
     */
    ezRegisterEnumeration(enumerationClazz) {
        if (!EzObject.isValid(enumerationClazz)) {
            throw new EzBadParamException(
                'enumerationClazz',
                EzApi.ezInstance,
                EzApi.ezInstance.ezRegisterEnumeration);
        }

        if (EzObject.isValid(enumerationClazz.ezInstance) && 0 > enumerationClazz.ezInstance.ezStates.indexOf(EzInstanceState.REGISTERED_WITH_EZAPI)) {
            // Already registered, ignore it
            return enumerationClazz.ezInstance;
        }

        enumerationClazz.ezInstance = new enumerationClazz();

        enumerationClazz.ezInstance.ezStates = EzArray.arrayOrEmpty(enumerationClazz.ezInstance.ezStates);

        if (0 > enumerationClazz.ezInstance.ezStates.indexOf(EzInstanceState.CONSTRUCTED)) {
            enumerationClazz.ezInstance.ezStates.push(EzInstanceState.CONSTRUCTED);
        }

        // Register with ezApi
        EzApi.ezInstance.enums[enumerationClazz.name] = enumerationClazz.ezInstance;

        EzApi.ezInstance.ezclocker[enumerationClazz.name] = enumerationClazz.ezInstance;

        enumerationClazz.ezInstance.ezStates.push('REGISTERED_WITH_EZAPI');

        enumerationClazz.ezInstance.ready = true;

        // Trigger the ready event for the enumeration
        if (EzObject.isValid(enumerationClazz.ezEventNames) && EzString.hasLength(enumerationClazz.ezEventNames.onReady)) {
            EzCustomEvent.build(enumerationClazz.ezEventNames.onReady)
                .ezDocumentTiggerEvent({
                    apiClass: enumerationClazz,
                    apiName: enumerationClazz.name,
                    registeredInstance: enumerationClazz.ezInstance,
                    ezApi: EzApi.ezInstance
                });
        } else {
            EzCustomEvent.build(enumerationClazz.constructor.name)
                .ezDocumentTiggerEvent({
                    apiClass: enumerationClazz,
                    apiName: enumerationClazz.name,
                    registeredInstance: enumerationClazz.ezInstance,
                    ezApi: EzApi.ezInstance
                });
        }

        return enumerationClazz.ezInstance;
    }

    /**
     * @public @method
     * Adds the UNKNOWN enum value to the provided enumeration (if not already present).
     * Adds the ezFromValue function to the provided enumerationInstance if not already available.
     * Adds the ezAsSet function to the provided enumeraionInstance if not already available.
     * Finally, stores enumerationInstance within EzApi.ezInstance.enums with the provided enumberationName.
     * @param {string} enumerationName
     * @param {object} enumerationInstance
     *
     * @return {object}
     *
     * @deprecated
     * Migrate to EzApi.ezInstance.ezRegisterEnumeration
     * Wil remove EzApi.ezInstance.ezStoreEnum in future release.
     */
    ezStoreEnum(enumerationName, enumerationInstance) {
        if (!EzString.hasLength(enumerationName)) {
            throw new EzBadParamException('enumerationName', 'EzApi', 'ezStoreEnum');
        }
        if (!EzObject.isValid(enumerationInstance)) {
            throw new EzBadParamException('enumerationInstance', 'EzApi', 'ezStoreEnum');
        }

        // Store the enumeration instance in ezApi
        EzApi.ezInstance.enums[enumerationName] = enumerationInstance;

        // Add the common properties
        if (!EzString.hasLength(enumerationInstance.constructor.UNKNOWN)) {
            enumerationInstance.constructor.UNKNOWN = 'UNKNOWN';
            enumerationInstance.UNKNOWN = enumerationInstance.constructor.UNKNOWN;
        }

        // Add the common methods
        if (!EzFunction.isFunction(enumerationInstance.constructor.getClass)) {
            enumerationInstance.constructor.ezGetClass = () => enumerationInstance.constructor;

            enumerationInstance.ezGetClass = enumerationInstance.constructor.ezGetClass;
        }

        if (!EzFunction.isFunction(enumerationInstance.constructor.toString)) {
            enumerationInstance.constructor.ezToString = () => `[${enumerationInstance.ezGetClass().name}]`;

            enumerationInstance.ezToString = enumerationInstance.constructor.ezToString;
        }

        if (!EzFunction.isFunction(enumerationInstance.constructor.ezFromValue)) {
            enumerationInstance.constructor.ezFromValue = (enumValue) => {
                if (!EzString.hasLength(enumValue)) {
                    return enumerationInstance.UNKNOWN;
                }

                if (EzObject.hasProperty(this, enumValue)) {
                    return enumerationInstance[enumValue];
                }

                const keys = Object.keys(this);

                for (const enumKey of keys) {
                    if (enumKey !== 'ENUM_NAME' && enumerationInstance[enumKey] === enumValue) {
                        return enumerationInstance[enumKey];
                    }
                }

                return enumerationInstance.UNKNOWN;
            };

            enumerationInstance.ezFromValue = enumerationInstance.constructor.ezFromValue;
        }

        if (!EzFunction.isFunction(enumerationInstance.constructor.ezAsSet)) {
            enumerationInstance.constructor.ezAsSet = (...args) => {
                const aSet = new Set();

                if (EzArray.hasLength(args)) {
                    for (const argValue of args) {
                        aSet.add(enumerationInstance.constructor.ezInstance.ezFromValue(argValue));
                    }
                }

                return aSet;
            };
            enumerationInstance.ezAsSet = enumerationInstance.constructor.ezAsSet;
        }

        if (!EzFunction.isFunction(enumerationInstance.constructor.ezTo)) {
            enumerationInstance.constructor.ezTo = (propName, enumValue) => {
                const self = enumerationInstance.constructor.ezInstance;

                if (!EzString.hasLength(enumValue) || -1 === self.ezKeys.indexOf(enumValue)) {
                    throw EzApi.ezInstance.ezException(
                        `Enumeration value of ${enumValue} is not a member of enumeration ${self.ezTypeName}.`);
                }

                const _EnumValue = EzApi.ezInstance.ezSingleLineTemplate`_${enumValue}`;

                if (!EzObject.hasProperty(self, _EnumValue)) {
                    throw EzApi.ezInstance.ezException(
                        `Enumeration ${self.ezTypeName} does not support additional properties for ${enumValue}`);
                }

                if (!EzObject.hasProperty(self[_EnumValue], propName)) {
                    throw EzApi.ezInstance.ezException(
                        EzString.em`
                            Additional value property or ${propName} for enumeration value ${enumValue} on enumeration
                            ${self.ezTypeName} is not available.`);
                }

                return self[_EnumValue][propName];
            };

            enumerationInstance.ezTo = enumerationInstance.constructor.ezTo;
        }


        // Return the stored instance
        enumerationInstance.ezStates = enumerationInstance.ezStates || [];

        enumerationInstance.ezStates.push('REGISTERED_WITH_EZAPI');

        return EzApi.ezInstance.enums[enumerationName];
    }

    /**
     * @public @method
     * Register an api with the EzApi system.
     *
     * @param {string} apiName
     * @param {object} apiInstance
     *
     * @returns {object}
     * Returns the Api instance that is provided
     *
     * @deprecated
     * Migrate to EzApi.ezInstance.ezRegisterNewApi or EzApi.ezInstance.ezRegisterInstance
     * Wil remove EzApi.ezInstance.ezRegisterApi in future release.
     */
    ezRegisterApi(apiName, apiInstance, apiClass) {
        if (!EzString.hasLength(apiName)) {
            throw new EzBadParamException(
                'apiName',
                EzApi.ezInstance,
                EzApi.ezInstance.ezRegisterApi);
        }
        if (!EzObject.isValid(apiInstance)) {
            throw new EzBadParamException(
                'apiInstance',
                EzApi.ezInstance,
                EzApi.ezInstance.ezRegisterApi);
        }

        if (EzObject.isValid(EzApi.ezInstance.ezclocker[apiName])) {
            if (EzBoolean.isTrue(EzApi.ezInstance.ezDebugMode)) {
                EzApi.ezInstance.ezLogError(
                    EzString.em`
                        EzApi: EzClocker API ${apiName} is already registered!
                        Please avoid registering an an api with the same name more than once.
                        Multiple registrations indicate a defect, duplicate api names, or duplicate imports of dependencies.`);
            } else {
                EzApi.ezInstance.ezLogWarn(`EzApi: Replacing already registered api ${apiName} with another instance.`);
            }

            EzApi.ezInstance.ezclocker[apiName] = apiInstance;

            EzApi.ezInstance.ezLogDebug(`EzApi: Replaced existing registration of api ${apiName}.`);
        } else {
            EzApi.ezInstance.ezclocker[apiName] = apiInstance;
            EzApi.ezInstance.ezLogDebug(`EzApi: Registered api with name=${apiName}.`);
        }

        let typeName = null;

        if (EzObject.isValid(apiClass)) {
            typeName = apiClass.constructor.name;
        } else if (EzApi.ezInstance.ezJsonObjectHasProperty(apiInstance, 'CLASS_NAME')) {
            typeName = apiInstance.CLASS_NAME;
        } else {
            typeName = apiName.replace(apiName.charAt(0), apiName.charAt(0).toUpperCase());
        }

        if (EzString.hasLength(typeName)) {
            // Apply common helper functions to the instance...
            apiInstance.toString = () => `[object ${typeName}]`;
        }

        return EzApi.ezInstance.ezclocker[apiName];
    }

    ezDetermineNameOfClass(aClazz, aClazzInstance, aClazzName) {
        if (EzObject.isValid(aClazz)) {
            return aClazz.name;
        }

        if (EzString.hasLength(aClazzInstance?.constructor?.name)) {
            return aClazzInstance.constructor.name;
        }

        if (EzString.hasLength(aClazzName)) {
            return aClazzName;
        }

        throw new EzException(
            EzString.em`
                Unable to determine the name of the class from the provided parameters:
                aClazz=${aClazz}, aClazzInstance=${aClazzInstance}, aClazzName=${aClazzName}`);
    }

    /**
     * @public @method
     * Attempts to determine the name of a class that can be used for registration in ezApi.
     *
     * @param {object} apiClass
     * @returns {string}
     */
    ezDetermineApiNameFromClass(apiClass) {
        let apiName;

        try {
            if (EzObject.isValid(apiClass.prototype.constructor.name)) {
                apiName = apiClass.prototype.constructor.name;
            } else {
                // Try to guess the old fashion way...
                const result = /^function\s+([\w$]+)\s*\(/.exec(apiClass.toString());

                apiName = result
                    ? result[1]
                    : EzString.EMPTY;
            }
        } catch (instanceNameError) {
            EzApi.ezInstance.ezLogError(
                EzString.em`
                    EzApi: Unable to determine the api instance name to use
                    from the provided api class/constructor ${apiClass.name}.
                    Reason: ${instanceNameError.name}: ${instanceNameError.message}.`);

            throw (instanceNameError);
        }

        if (!EzString.hasLength(apiName)) {
            throw EzApi.ezInstance.ezException(
                'Unable to determine the name from the constructor of an API class. Registration cannot continue.');
        }

        return apiName.replace(apiName.charAt(0), apiName.charAt(0).toLowerCase());
    }

    /**
     * @public @method
     * Creates a new instance of the provided class.
     * Adds a special toString method to return the constructor name of the class as well.
     *
     * @param {Class} aClass
     * @returns {object}
     */
    ezNewInstance(aClass) {
        let apiInstance;

        try {
            apiInstance = new aClass.prototype.constructor();

            apiInstance.toString = () => `[object ${aClass.prototype.constructor.name}]`;

            return apiInstance;
        } catch (constructError) {
            EzApi.ezInstance.ezLogError(
                EzString.em`
                    Creation of new instance of ${aClass.name} failed.
                    Reason: ${constructError.name}::${constructError.message}`);

            throw (constructError);
        }
    }

    /**
     * @public @method @method
     * Registers a new api instance from the provided class. Calls the objects ezInit() method if it exists. Returns the
     * new api instance.
     *
     * @param {Class} Clazz
     * @param {string} apiName
     * @param {boolean} overrideReRegistration
     * Default: false
     * @param {undefined|null|array} aliasApiNames;
     * Default: null
     *
     * @returns {object}
     */
    ezRegisterNewApi(Clazz, apiName, overrideReRegistration = false, aliasApiNames = null) {
        if (!EzObject.isValid(Clazz)) {
            throw new EzBadParamException(
                'Clazz',
                EzApi.ezInstance,
                EzApi.ezInstance.ezRegisterNewApi);
        }

        if (!EzString.hasLength(apiName)) {
            apiName = EzApi.ezInstance.ezDetermineApiNameFromClass(Clazz);
        }

        if (EzObject.isValid(EzApi.ezInstance.ezclocker?.[apiName])) {
            if (EzBoolean.isTrue(overrideReRegistration)) {
                // Allow allow multiple registrations only if overrideReRegistration is true.
                // THIS IS A RARE CASE
                EzApi.ezInstance.ezLogWarn(
                    EzString.em`
                        Detected forced Re-registration of a previously registered class in EzApi.
                        Will replace the existing registration in EzApi with a new initialization of a singleton instance for globalThis.ezApi.${apiName}.`);
            } else {
                // Already registered, do not allow multiple registrations
                if (EzBoolean.isTrue(EzApi.ezInstance.ezDebugMode)) {
                    // Log warning when in debug mode
                    EzApi.ezInstance.ezLogError(
                        EzString.em`
                            Detected attempt to register an previously registerd singleton instance in EzApi.
                            Will ignore the registration request. However, you can force re-registration by passign the EzApi.ezRegisterNewApi method's
                            third parameter of 'overrideExistingRegistration' as true.`);
                }

                return Clazz.ezInstance;
            }
        }

        // Step 1: Create the instance
        Clazz.ezInstance = EzApi.ezInstance.ezNewInstance(Clazz);

        // Step 2: Make sure it has ezStates as an array
        Clazz.ezInstance.ezStates = EzArray.arrayOrEmpty(Clazz.ezInstance.ezStates);

        // Step 3: Register with ezApi
        // ---------------------------------------------------------------------
        // >>> IMPORTANT <<<
        // Registration with ezApi must happen before ezInit is called.
        // ---------------------------------------------------------------------
        EzApi.ezInstance.ezclocker[apiName] = Clazz.ezInstance;

        // Step 4: Set ezDebugMode if supported by api class
        if (EzBoolean.isBoolean(Clazz?.ezInstance?.ezDebugMode)) {
            Clazz.ezInstance.ezDebugMode = EzBoolean.booleanOrFalse(EzApi.ezInstance.ezDebugMode);
        }

        // Step 4: Update ezStates
        // String values instead of the enum used here to avoid having to import the enumeration
        // and possibly causing an import loop.
        if (!Clazz.ezInstance.ezStates.includes('REGISTERED_WITH_EZAPI')) {
            Clazz.ezInstance.ezStates.push('REGISTERED_WITH_EZAPI');
        }

        // Step 5: Call the ezInit function if the api class provides it
        if (EzFunction.isFunction(Clazz.ezInstance?.ezInit)) {
            try {
                Clazz.ezInstance.ezInit();

                if (Clazz.ezInstance.ezStates.includes(EzInstanceState.NOT_INITIALIZED)) {
                    Clazz.ezInstance.ezStates = EzArray.removeAll(EzInstanceState.NOT_INITIALIZED);
                }

                // String values instead of the enum used here to avoid having to import the enumeration
                // and possibly causing an import loop.
                if (!Clazz.ezInstance.ezStates.includes(EzInstanceState.INITIALIZED)) {
                    // Add that ezInit was called to the instance's states if not already there.
                    Clazz.ezInstance.ezStates.push(EzInstanceState.INITIALIZED);
                }

                Clazz.ezApiRegistrationState = EzRegistrationState.REGISTERED;
            } catch (initError) {
                EzApi.ezInstance.ezLogError(
                    EzString.em`
                        EzApi.ezRegisterNewApi call to ${Clazz.ezInstance.ezInit.toString()} method failed for
                        apiName='${apiName}'.
                        [Error reported: ${EzString.stringOrDefault(initError.message, 'no additional details provided')}]`);

                throw (initError);
            }
        } else if (EzBoolean.isTrue(EzApi.ezInstance?.ezDebugMode)) {
            // Warn if an ezInit method was not available on the class
            EzApi.ezInstance.ezLogWarn(
                EzString.msg`
                    Skipped automatic initialization of EzApi registration for apiName=${apiName}
                    due to class ${Clazz.constructor.name} not providing it's own ezInit() method.`);
        }

        // Step 6: Register any alias names specified
        if (EzArray.hasLength(aliasApiNames)) {
            for (const aliasApiName of aliasApiNames) {
                EzApi.ezInstance.ezclocker[aliasApiName] = Clazz.ezInstance;
            }
        }

        if (!EzObject.isValid(Clazz?.ezEventNames) || !EzString.hasLength(Clazz.ezEventNames?.onReady)) {
            const classOnReadyEventName = EzString.hasLength(Clazz?.name)
                ? Clazz?.name
                : `${Clazz.ezApiName.substr(0, 1).toUpperCase()}${Clazz.ezApiName.substr(1)}`;

            if (!EzObject.isValid(Clazz?.ezEventNames)) {
                Clazz.ezEventNames = {
                    onReady: classOnReadyEventName
                };
            } else {
                Clazz.ezEventNames[onReady] = classOnReadyEventName;
            }

            EzApi.ezInstance.ezLogDebug(
                EzString.msg`
                    Detected that class ${Clazz.constructor.name} does not provide the ezEventNames property and/or the
                    onReady event name. Therefore, EzApi guessed the onReady event name as '${classOnReadyEventName}'
                    for API registration with apiName=${apiName}.`);
        }

        // Assign global aliases:
        // Global reference to the Class
        window[Clazz.constructor.name] = Clazz;

        // Global reference to the singleton instance
        window[apiName] = Clazz.ezInstance;

        // After ezInit, the class api is ready to use
        Clazz.ezSingletonReady = true;

        Clazz.ezInstance.ready = true;

        const onReadyEventData = {
            apiClass: Clazz,
            registeredInstance: apiName.ezInstance,
            ezApi: EzApi.ezInstance,
            apiName
        };

        EzCustomEvent.build(
            Clazz.ezEventNames.onReady,
            true)
            .ezDocumentTiggerEvent(onReadyEventData);

        // If the EzEventEngine is ready, then also trigger the API class's onReady event via EzEventEngine
        if (EzObject.isValid(EzApi?.ezInstance?.ezclocker?.ezEventEngine)) {
            if (!EzApi.ezInstance.ezclocker.ezEventEngine.ezIsEventRegistered(Clazz.ezEventNames.onReady)) {
                EzApi.ezInstance.ezclocker.ezEventEngine.ezRegisterEvent(
                    apiName,
                    Clazz.ezEventNames.onReady);
            }


            EzApi.ezInstance.ezclocker.ezEventEngine.ezTriggerEvent(
                Clazz.ezEventNames.onReady,
                EzApi.ezInstance.ezclocker.ezEventEngine.ezBuildEventData(
                    apiName,
                    `Singleton instance registered with EzApi and ready to use.`,
                    onReadyEventData));
        }

        EzApi.ezInstance.ezLogDebug(`Successfully registered API with apiName=${apiName} for class ${Clazz.constructor.name} and triggered the onReady event.`);

        return Clazz.ezInstance;
    }

    /**
     * @public @method
     * Registers a named instance of an object. Does not call any initialization methods.
     *
     * @param {string} instanceName
     * @param {object} instanceRef
     */
    ezRegisterInstance(instanceName, instanceRef) {
        if (!EzString.hasLength(instanceName)) {
            throw new EzBadParamException(
                'instanceName',
                EzApi.ezInstance,
                EzApi.ezInstance.ezRegisterInstance);
        }
        if (!EzObject.isValid(instanceRef)) {
            throw new EzBadParamException(
                'instanceRef',
                EzApi.ezInstance,
                EzApi.ezInstance.ezRegisterInstance);
        }

        instanceRef.ezInstanceName = instanceName;

        instanceRef.ezStates = instanceRef.ezStates || [];

        if (EzObject.hasProperty(instanceRef, 'ezDebugMode')) {
            instanceRef.ezDebugMode = EzApi.ezInstance.ezDebugMode;
        }

        // Set the classes ezApiRegistrationState to registered
        instanceRef['ready'] = true;

        // Create the new instance
        instanceRef['ezEventNames'] = instanceRef['ezEventNames'] || {};

        if (!EzObject.hasProperty(instanceRef['ezEventNames'], 'onReady')) {
            instanceRef['ezEventNames']['onReady'] = `ezOn_${instanceRef.name}_Ready`;
        }

        EzApi.ezInstance.ezclocker[instanceName] = instanceRef;

        if (!EzApi.ezInstance.ezclocker[instanceName].ezStates.includes('REGISTERED_WITH_EZAPI')) {
            EzApi.ezInstance.ezclocker[instanceName].ezStates.push('REGISTERED_WITH_EZAPI');
        }

        instanceRef['ezApiRegistrationState'] = 'REGISTERED';

        // If the instance has defined a value fo ezOnReadyEventName, then
        // fire the event via the document.
        document.dispatchEvent(
            new CustomEvent(
                instanceRef['ezEventNames']['onReady'],
                {
                    bubbles: true,
                    apiClass: instanceRef,
                    apiName: instanceName,
                    registeredInstance: EzApi.ezInstance.ezclocker[instanceName],
                    ezApi: EzApi.ezInstance
                }));

        EzApi.ezInstance.ezLogDebug(`[EzApi: Registered API ${instanceName}]`);
        return EzApi.ezInstance.ezclocker[instanceName];
    }

    /**
     * @public @method
     * Unregisters an api from ezApi (if it exists)
     *
     * @returns {boolean}
     * True if the API was unregisterd, false otherwise
     */
    ezUnRegisterApi(apiName) {
        if (!EzString.hasLength(apiName)) {
            throw new EzBadParamException(
                'apiName',
                EzApi.ezInstance,
                EzApi.ezInstance.ezUnRegisterApi);
        }

        if (!EzObject.isValid(EzApi.ezInstance.ezclocker[apiName])) {
            if (EzBoolean.isTrue(EzApi.ezInstance.ezDebugMode)) {
                EzApi.ezInstance.ezLogError(
                    `EzApi: No need to unregister the api with name=${apiName} because it is not registerd with ezApi.`);
            }

            return false;
        }

        try {
            delete EzApi.ezInstance.ezclocker[apiName];

            EzApi.ezInstance.ezLogDebug(`EzApi: Unregistered api ${apiName}.`);

            return true;
        } catch (err) {
            EzApi.ezInstance.ezLogDebug(
                EzString.em`
                    EzApi: Unregistration of api with name=${apiName} failed.
                    Reason: ${err.name}: ${err.message}`);

            throw (err);
        }
    }

    /**
     * @public @method
     * Registers theezApi as a global (root level) api. Avoid registering an Api on on the window if possible.
     *
     * @param {string} apiName
     * Name of the API object
     * @param {object} apiInstance
     * Instance of the API object
     *
     * @returns {object}
     * Returns the api instance provided
     *
     * @deprecated
     * Migrate to EzApi.ezInstance.ezRegisterNewApi or EzApi.ezInstance.ezRegisterInstance
     * Wil remove EzApi.ezInstance.ezRegisterApi in future release.
     */
    ezRegisterWindow(apiName, apiInstance) {
        if (!EzString.hasLength(apiName)) {
            throw new EzBadParamException(
                'apiName',
                EzApi.ezInstance,
                EzApi.ezInstance.ezRegisterWindow);
        }
        if (!EzObject.isValid(apiInstance)) {
            throw new EzBadParamException(
                'apiInstance',
                EzApi.ezInstance,
                EzApi.ezInstance.ezRegisterWindow);
        }

        // Window level API is NOT uppercase
        window[apiName] = apiInstance;

        return window[apiName];
    }

    /**
     * @public @method
     * Determines if an api exists in the EzApi system.
     *
     * @param {string} apiName
     *
     * @returns {boolean}
     */
    ezApiExist(apiName) {
        if (!EzString.hasLength(apiName)) {
            return false;
        }

        if (!EzObject.hasProperty(EzApi.ezInstance.ezclocker, apiName)) {
            return false;
        }

        let theApi = EzApi.ezInstance.ezclocker[apiName];

        if (!EzObject.isValid(theApi)) {
            if (!EzObject.hasProperty(window, apiName)) {
                return false;
            }

            theApi = window[apiName];
        }

        return EzObject.isValid(theApi);
    }

    /**
     * @public @method
     * Registers a global categorized event for the owner
     * If eventOwnerName OR eventName is empty or null the event will NOT register and the return is null.
     * If the eventCategory is not provided, then onEvent is used.
     *
     * @param {string} eventOwnerName
     * @param {string|null} eventCategory
     * Builds eventCategory value is transformed to camel case when registering and can only be one of the following values:
     * onReady, onClick, onValidate, onShow, onClose, onCancel, onRefresh, onSuccess, onFailure,
     * or onEvent (the default category)
     * @param {string} eventName
     *
     * @returns {string|null}
     * Returns the event's registration name: {eventOwnerName}.{eventCategory}
     *
     * @deprecated
     * Migrate to EzEventEngine functionality Wil remove EzApi.ezInstance.ezRegisterGlobalEvent in future release.
     */
    ezRegisterGlobalEvent(eventOwnerName, eventCategory, eventName) {
        if (!EzString.hasLength(eventOwnerName) || !EzString.hasLength(eventName)) {
            throw new EzBadParamException(
                'eventOwnerName',
                EzApi.ezInstance,
                EzApi.ezInstance.ezRegisterGlobalEvent);
        }

        let useEventCategory = EzString.hasLength(eventCategory)
            ? eventCategory.toLowerCase()
            : 'onevent';

        switch (useEventCategory) {
            case 'ondone':
                useEventCategory = 'onDone';

                break;
            case 'onerror':
                useEventCategory = 'onError';

                break;
            case 'onsubmitted':
                useEventCategory = 'onSubmitted';

                break;
            case 'onsave':
                useEventCategory = 'onSave';

                break;
            case 'onsaved':
                useEventCategory = 'onSave';

                break;
            case 'onauthorize':
                useEventCategory = 'onAuthorize';

                break;
            case 'onaction':
                useEventCategory = 'onAction';

                break;
            case 'onfinished':
                useEventCategory = 'onFinished';

                break;
            case 'onready':
                useEventCategory = 'onReady';

                break;
            case 'onafter':
                useEventCategory = 'onAfter';

                break;
            case 'onbefore':
                useEventCategory = 'onBefore';

                break;
            case 'onclick':
                useEventCategory = 'onClick';

                break;
            case 'onvalidate':
                useEventCategory = 'onValidate';

                break;
            case 'onshow':
                useEventCategory = 'onShow';

                break;
            case 'onchanged':
                useEventCategory = 'onChanged';

                break;
            case 'onupdate':
                useEventCategory = 'onUpdate';

                break;
            case 'onupdated':
                useEventCategory = 'onUpdated';

                break;
            case 'onchange':
                useEventCategory = 'onChange';

                break;
            case 'onclosed':
                useEventCategory = 'onClosed';

                break;
            case 'onclose':
                useEventCategory = 'onClose';

                break;
            case 'oncancel':
                useEventCategory = 'onCancel';

                break;
            case 'onrefresh':
                useEventCategory = 'onRefresh';

                break;
            case 'onsuccess':
                useEventCategory = 'onSuccess';

                break;
            case 'onfailure':
                useEventCategory = 'onFailure';

                break;
            case 'onload':
                useEventCategory = 'onLoad';

                break;
            case 'onloaded':
                useEventCategory = 'onLoaded';

                break;
            case 'onevent':
            default:
                if (EzBoolean.isTrue(EzApi.ezInstance.ezDebugMode)) {
                    EzApi.ezInstance.ezLogWarn(
                        EzString.em`
                            EzApi: Detected an unknown event category of ${useEventCategory}
                            for eventName=${eventName} and eventOwner=${eventOwnerName}.
                            Will use the default category of "onEvent" as a fallback.`);
                }

                useEventCategory = 'onEvent';

                break;
        }

        const eventRegName = `${eventOwnerName}.${useEventCategory}`;

        EzApi.ezInstance.ezLogDebug(`EzApi: Registered global event: ${eventRegName}=${eventName}`);

        if (EzApi.ezInstance.ezIsNotEmptyArray(EzApi.ezInstance.events)) {
            EzApi.ezInstance.events[eventRegName].push(eventName);
        } else {
            EzApi.ezInstance.events[eventRegName] = [];
            EzApi.ezInstance.events[eventRegName].push(eventName);
        }

        return `${eventRegName}.${eventName}`;
    }

    /**
     * @public @method
     * Generates an ezClocker objectid in the format: 'EzId' + new Date().getTime()
     *
     * @returns {string}
     */
    ezGenerateObjectId() {
        return 'EzId' + new Date().getTime();
    }

    /**
     * @public @method
     * Gets the UTC offset for the local time zone
     *
     * @returns {string}
     *
     * @deprecated
     * Use EzDateTime functionality instead.
     * Will remove in future release.
     */
    getLocalUTCOffset() {
        if (EzObject.isValid(moment)) {
            return moment.tz(EzApi.ezInstance.ezGetPreferredTimeZone()).utcOffset();
        }

        const offset = new Date().getTimezoneOffset();

        const minutes = Math.abs(offset);

        const hours = Math.floor(minutes / 60);

        const prefix = 0 > offset
            ? '+'
            : '-';

        return prefix + hours;
    }

    /**
     * @public @method
     * Returns the local browser timezone id - if possible
     *
     * @returns {string}
     *
     * @deprecated
     * Use EzDateTime functionality instead.
     * Will remove in future release.
     */
    getLocalTimeZoneId() {
        if (EzObject.isValid(moment) && EzObject.isValid(moment.tz)) {
            return moment.tz.guess();
        }

        return 'UTC';
    }

    /**
     * @public @method
     * Creates a timestamp with optional prefix and suffix text in the format: {prefix-}YYYY.MM.kk.mm{-suffix}
     *
     * @returns {string}
     *
     * @deprecated
     * Use EzDateTime functionality instead.
     * Will remove in future release.
     */
    ezCreateTimeStamp(prefix, suffix) {
        const pre = EzString.hasLength(prefix)
            ? `${prefix}-`
            : EzString.EMPTY;

        const suf = EzString.hasLength(suffix)
            ? `-${suffix}`
            : EzString.EMPTY;

        return `${pre}${moment.tz(EzApi.ezInstance.ezGetPreferredTimezone()).format('YYYY.MM.kk.mm')}${suf}`;
    }

    /**
     * @public @method
     * Gets the user's preferred timezone
     *
     * @returns {string}
     * User's preferred timezone
     */
    ezGetPreferredTimeZone() {
        if (!EzString.hasLength(EzApi.ezInstance.ezPreferredTimeZone)) {
            if (EzObject.isValid(moment) && EzObject.isValid(moment.tz)) {
                EzApi.ezInstance.ezPreferredTimeZone = moment.tz.guess(true);
            } else {
                EzApi.ezInstance.ezPreferredTimeZone = new Date().getTimezoneOffset();
            }
        }

        return self.ezPreferredTimeZone;
    }

    /**
     * @public @method @method
     * Guesses the method name from the provided reference of string name. If a method name
     * is not possible from methodRefOrName then the operonal defaultName is returned OR one of the
     * following strings: '[unknown class]' or '[null or undefined class]'
     *
     * @param {undefined|null|Object|String} classInstanceOrName
     * @param {undefiend|null|String} defaultName
     *
     * @returns {String}
     */
    ezGuessClassName(classInstanceOrName, defaultName) {
        if (EzObject.isValid(classInstanceOrName)) {
            if (EzApi.ezInstance.ezIsString(classInstanceOrName)) {
                return EzString.hasLength(classInstanceOrName)
                    ? classInstanceOrName
                    : '[unknown class]';
            } else if (EzApi.ezInstance.ezIsFunction(classInstanceOrName)) {
                return classInstanceOrName.prototype.constructor.name;
            } else if (EzApi.ezInstance.ezIsObject(classInstanceOrName)) {
                return classInstanceOrName.constructor.name;
            }

            return EzString.hasLength(defaultName)
                ? defaultName
                : '[unknown class]';
        }

        return EzString.hasLength(defaultName)
            ? defaultName
            : '[null or undefined class]';
    }

    /**
     * @public @method @method
     * Guesses the method name from the provided reference of string name. If a method name
     * is not possible from methodRefOrName then the operonal defaultName is returned OR one of the
     * following strings: '[unknown_method]()' or '[null or undefined method]()'
     *
     * @param {undefined|null|Object|string} methodRefOrName
     * @param {undefiend|null|String} defaultName
     *
     * @returns {String}
     */
    ezGuessMethodName(methodRefOrName, defaultName) {
        if (EzObject.isValid(methodRefOrName)) {
            if (EzApi.ezInstance.ezIsFunction(methodRefOrName)) {
                return EzBadParamException.functionToMethodNameAndParams(methodRefOrName);
            } else if (EzApi.ezInstance.ezIsString(methodRefOrName) && 0 < methodRefOrName.length) {
                return methodRefOrName;
            }

            return EzString.hasLength(defaultName)
                ? defaultName
                : '[unknown_method]()';
        }

        return EzString.hasLength(defaultName)
            ? defaultName
            : '[null or undefined method]()';
    }

    /**
     * Transforms the provided errorEntity into an error message that includes the
     * option userMsg and the className and/or methodName where the error occurred.
     * @param {undefiend|null|object} errorEntity
     * @param {undefiend|null|string} userMsg
     * @param {undefiend|null|object|string} classInstanceOrName
     * @param {undefiend|null|function|string} methodNameOrRef
     */
    ezErrorEntityAsErrorMessage(errorEntity, userMsg, classInstanceOrName, methodNameOrRef) {
        let errorMsgPrefix = EzString.hasLength(userMsg)
            ? userMsg
            : EzString.EMPTY;

        let errorCode = '500';

        let additionalDetails = EzString.EMPTY;

        let stackMsg = EzString.EMPTY;

        if (EzObject.isValid(errorEntity)) {

            if (EzObject.isValid(errorEntity.errorCode)) {
                if (EzApi.ezInstance.ezIsNumber(errorEntity.errorCode)) {
                    errorCode = errorEntity.errorCode.toString();
                } else if (EzApi.ezInstance.ezIsString(errorEntity.errorCode)) {
                    errorCode = errorEntity.errorCode;
                } else {
                    errorCode = EzJson.toJson(errorEntity.errorCode);
                }
            }

            const entityJson = EzJson.toJson(errorEntity);

            if (EzString.hasLength(entityJson) && '{}' !== entityJson) {
                additionalDetails = `Additional details: ${entityJson}`;
            }

            if (EzObject.isValid(errorEntity.stack)) {
                stackMsg = `Stack: ${errorEntity.stack}`;
            }
        }

        const className = EzApi.ezInstance.ezGuessClassName(classInstanceOrName, EzString.EMPTY);

        const classMethod = EzApi.ezInstance.ezGuessMethodName(methodNameOrRef, EzString.EMPTY);

        let classAndMethod;

        if (EzString.hasLength(classMethod)) {
            classAndMethod = EzString.hasLength(className)
                ? `${className}.${classMethod}`
                : classMethod;
        } else {
            classAndMethod = className;
        }

        if (EzString.hasLength(classAndMethod)) {
            errorMsgPrefix = EzString.hasLength(errorMsgPrefix)
                ? `Error [${errorCode}]:[${classAndMethod}] ${errorMsgPrefix}`
                : `Error [${errorCode}]:An unexpected error occurred in ${classAndMethod}.`;
        } else {
            errorMsgPrefix = EzString.hasLength(errorMsgPrefix)
                ? `Error [${errorCode}] ${errorMsgPrefix}`
                : `Error [${errorCode}] An unexpected error occurred.`;
        }

        return `${errorMsgPrefix}, ${additionalDetails}, ${stackMsg}`;
    }

    /**
     * @public @method
     * Converts the item to JSON if it is NOT a string. Otherwise, returns the string.
     *
     * @param {object} item
     *
     * @returns {string|object}
     * JSON string version of the provided item OR the actual value of the item if it cannot convert to json.
     */
    ezToJson(item, indentValue, htmlDisplay) {
        if (EzApi.ezInstance.ezIsString(item)) {
            return item;
        }

        if (!EzObject.isValid(item)) {
            return '{}';
        }

        if (EzFunction.isFunction(item.ezToJSON)) {
            // Has it's own ezToJson serilization method
            return item.ezToJSON(indentValue, htmlDisplay);
        }

        if (EzFunction.isFunction(item.toJson)) {
            // The item object has a legacy toJson method to perform it's own JSON serilization.
            return item.toJson();
        }

        try {
            if (EzBoolean.isTrue(htmlDisplay)) {
                return JSON.stringify(item, null, '&nbsp;').replace('\n', '<br/>');
            }

            if (EzApi.ezInstance.ezIsNumber(indentValue)) {
                return JSON.stringify(item, null, indentValue);
            }

            const jsonString = JSON.stringify(item);

            return 2 === jsonString.length && '{}' === jsonString && '{}' !== item
                // Probably not a JSON object
                ? item
                : jsonString;
        } catch (ex) {
            EzApi.ezInstance.ezLogInfo(`Failed to convert "${item}" to json.`);

            return item;
        }
    }

    /**
     * @public @method
     * Returns and JS object created from the provided JSON string
     *
     * @param {string} jsonString
     *
     * @returns {string}
     */
    ezFromJson(jsonString) {
        if (!EzString.hasLength(jsonString)) {
            return jsonString;
        }

        try {
            return JSON.parse(jsonString);
        } catch (ex) {
            EzApi.ezInstance.ezLogInfo(`Failed to convert "${jsonString}" to a Javascript object.`);

            return jsonString;
        }
    }

    /**
     * @public @method
     * Builds a default exception json message (similar to what service would return with)
     *
     * @param {string} errorCode
     * @param {string} message
     * @param {object} ex
     *
     * @returns {object}
     */
    buildJsonError(errorCode, message, ex) {
        if (EzObject.isValid(ex)) {
            message = EzString.hasLength(message)
                ? `${message} Error: ${EzJson.toJson(ex)}`
                : ` Error: ${EzJson.toJson(ex)}`;
        } else {
            message = EzString.hasLength(message)
                ? message
                : 'Failed';
        }

        return EzApi.ezInstance.buildBasicJsonResponse(
            EzObject.isValid(errorCode)
                ? errorCode
                : '500',
            message);
    }

    /**
     * @public @method
     * Builds the basic JSON response object with errorCode = 0
     *
     * @param {string} response
     *
     * @returns {object}
     * {
     *     errorCode: '0',
     *     messasge: {response|'Success'}
     * }
     */
    buildBasicJsonResponse(response) {
        return {
            errorCode: '0',
            message: EzString.hasLength(response)
                ? response
                : 'Success'
        };
    }

    /**
     * @public @method
     *
     * @param {Number=500} errorCode
     * @param {String='No error message provided.'} errorMessage
     *
     * @returns {object}
     * {
     *     errorCode: {errorCode|500}
     *     message: {errorMessage|'No error message provided.'}
     * }
     */
    ezBuildEzClockerErrorResponse(errorCode, errorMessage) {
        return {
            'errorCode': EzApi.ezInstance.ezIsNumber(errorCode)
                ? errorCode
                : 500,
            'message': !EzString.hasLength(errorMessage)
                ? 'No error message provided.'
                : errorMessage
        };
    }

    /**
     * @public @method
     * Gets the passed item as a json string. If the item is already a string, the string is simply reflected back as is.
     *
     * @param {*} jsonItem
     *
     * @returns {string}
     *
     * @deprecated
     * Migrate to EzJson.toJson()
     * Will remove in future release.
     */
    gJsonString(jsonItem) {
        return EzJson.toJson(jsonItem);
    }

    /**
     * @public @method
     * Gets a Javascript object jsonItem is a json string. Otherwise, returns the object as is.
     *
     * @returns {object}
     *
     * @deprecated
     * Migrate to EzJson.toJson()
     * Will remove in future release.
     */
    gJsonObject(jsonItem) {
        return EzApi.ezInstance.ezFromJson(jsonItem);
    }

    /**
     * @public @method
     * Adds the key property with value to the provided jsonObject. If jsonObject is null an empty json object is used.
     *
     * @param {object} jsonObject
     * @param {string} key
     * @param {*} value
     *
     * @returns {object}
     */
    ezAddJsonProp(jsonObject, key, value) {
        jsonObject = EzObject.isValid(jsonObject)
            ? jsonObject
            : {};

        if (!EzString.hasLength(key)) {
            return jsonObject;
        }

        jsonObject[key] = EzObject.isValid(value)
            ? value
            : EzString.EMPTY;

        return jsonObject;
    }

    /**
     * @public @method
     * If condition is true, then result of aCallback() is returned. Otherwise, the result of bCallback() is returned. If
     * the calling callback is null, null is returned.
     *
     * @param {boolean} condition
     * @param {Function} aCallback
     * @param {Function} bCallback
     *
     * @returns {*}
     */
    callAelseB(condition, aCallback, bCallback) {
        if (EzBoolean.isTrue(condition)) {
            if (EzFunction.isFunction(aCallback)) {
                return aCallback();
            }
        } else if (EzFunction.isFunction(bCallback)) {
            return bCallback();
        }

        return null;
    }

    /**
     * @public @method
     * Wrapps up $('#' + id) jquery syntax and allows for caching of the references
     * Always assumes single id passed!
     *
     * @param {string} id
     *
     * @deprecated
     * Migrate to globalThis.ezApi.ezUi.ezId(...)
     * Will remove in future release.
     */
    ez(id) {
        return $(`#${id}`);
    }

    /**
     * @public @method
     * Alternate wrapper for jquery $(#..) calls
     * Always assumes single id passed!
     *
     * @param {string} id
     *
     * @deprecated
     * Migrate to EzUI.ezInstance.ezId()
     * Will remove in future release.
     */
    ezId(id) {
        return $(`#${id}`);
    }

    /**
     * @public @method @method
     * Alternate wrapper for jquery $(#..) calls
     * Always assumes single id passed!
     * @param {string} id
     * @deprecated
     * Will remove in future release.
     * Migrate to EzUI.ezInstance.ezId()
     */
    ezId$(id) {
        return $(`#${id}`);
    }

    /**
     * Will remove in future release.
     * @public @method
     * Alternate wrapper for $() call
     * @param {string} jQuerySelector
     * @deprecated
     * Will remove in future release.
     * Migrate to EzUx.$(jQuerySelector)
     */
    ez$(jQuerySelector) {
        return $(jQuerySelector);
    }

    /**
     * @deprecated
     * Migrate to EzUI.ezInstance.ezId()
     * Will remove in future release.
     * @public @method
     * Alternate wrapper for $() call
     * @param {string} id
     */
    ezJQuery(jQuerySelector) {
        return $(jQuerySelector);
    }

    /**
     * @deprecated
     * Migrate to EzUI.ezInstance.ezId()
     * Will remove in future release.
     * @public @method
     * Returns the element identified by the provided jQuery selector. If the element does not exist,
     * null is returned instead.
     * @param {string|null} id
     */
    ezEid(id) {
        if (!EzString.hasLength(id)) {
            return null;
        }

        return EzApi.ezInstance.ezEid$(`#${id}`);
    }

    /**
     * @deprecated
     * Migrate to EzUI.ezInstance.ezId()
     * Will remove in future release.
     * @public @method
     * Returns the element identified by the provided jQuery selector. If the element does not exist,
     * null is returned instead.
     * @param {string|null} jQuerySelector
     */
    ezEid$(jQuerySelector) {
        if (!EzObject.isValid(jQuerySelector)) {
            return null;
        }

        const element = EzApi.ezInstance.ez$(jQuerySelector);

        return EzObject.isValid(element) && 0 !== element.length
            ? element
            : null;
    }

    /**
     * @deprecated
     * Migrate to EzUI.ezInstance.ezId('document')
     * Will remove in future release.
     * @public @method
     * Returns the referehce to the document either via $('document') or if no jquery available, then returns
        the global window.document.
     */
    ezDocument() {
        return EzObject.isValid($)
            ? $(document)
            : window.document;
    }

    /**
     * @deprecated
     * Migrate to EzUI.ezInstance.ezId('body')
     * Will remove in future release.
     * @public @method
     * Returns the reference to the document's body. Either returns iva $('body') via jquery, or if no jquery is available
        returns window.document.body
     */
    ezBody() {
        return EzObject.isValid($)
            ? $('body')
            : window.document.body;
    }

    /**
     * @deprecated
     * Migrate to EzUI.ezInstance.ezId('window') or globalThis or window
     * Will remove in future release.
     * @public @method
     * Wraps access to the window
     */
    ezWindow() {
        return EzObject.isValid($)
            ? $(window)
            : window;
    }

    /**
     * @deprecated
     * Stop all use
     * Will remove in future release.
     * @public @method
     * Builds the single auto-closed html tag
        Code:
        EzApi.ezInstance.tag('br');
        Output:
         <br/>
     * @param {string} tagName
     */
    tag(tagName) {
        return `<${tagName}/>`;
    }

    /**
     * @deprecated
     * Stop all use
     * Will remove in future release.
     * @public @method
     * Builds a starting html tag
     * Code:
     * EzApi.ezInstance.stag('body');
     * Output:
     *  <body>
     * @param {string} tagName
     */
    stag(tagName, params) {
        let htmlTag = `<${tagName}`;

        if (EzObject.isValid(params)) {
            Object.keys(params).forEach(function(key) {
                const value = EzString.stringOrDefault(params[key], key);

                if (EzString.hasLength(value)) {
                    htmlTag = `${htmlTag} ${key}="${value}"`;
                } else if (!EzApi.ezInstance.isUndefined(value) && EzApi.ezInstance.isNull(value)) {
                    htmlTag = `${htmlTag} ${key}`;
                }
            });
        }

        return `${htmlTag}>`;
    }

    /**
     * @deprecated
     * Stop all use
     * Will remove in future release.
     * @public @method
     * Builds an HTML ending tag.
     * Code:
     * EzApi.ezInstance.stag('body') +EzApi.ezInstance.etag('body');
     * Output:
     *  <body></body>
     * @param {string} tagName
     */
    etag(tagName) {
        return `</${tagName}>`;
    }

    /**
     * @deprecated
     * Stop all use
     * Will remove in future release.
     * @public @method
     * Builds an complete html tag with optional params
     * and content.
     * Code:
     * EzApi.ezInstance.htmlTag('a', [{href: 'https://ezclocker.com'}], 'Click');
     * Output:
     *  <a href="https://ezclocker.com">Click</a>
     * @param {string} tagName
     * @param {object} params
     * @param {string} content
     */
    htmlTag(tagName, params, content) {
        let htmlTag = `<${tagName}}`;

        if (EzObject.isValid(params)) {
            Object.keys(params).forEach(
                (key) => {
                    const value = EzString.stringOrDefault(params[key], params[key]);

                    if (!EzApi.ezInstance.isUndefined(value)) {
                        if (EzString.hasLength(value)) {
                            htmlTag = `${htmlTag} ${key}="${value}"`;
                        } else if (undefined !== value && null == value) {
                            htmlTag = `${htmlTag} ${key}`;
                        }
                    }
                });
        }

        htmlTag = `${htmlTag}>`;

        if (EzObject.isValid(content)) {
            htmlTag = `${htmlTag}\n\t${content}\n`;
        }

        return `${htmlTag}</${tagName}>\n`;
    }

    /**
     * @deprecated
     * Stop all use
     * Will remove in future release.
     * @public @method
     * Builds an HTML tag param string with leading space.
        Code:
         let link = '<a' +EzApi.ezInstance.tagParam('href', 'https://ezclocker.com') + '>Click</a>';
        Produces:
         <a href="https://ezclocker.com">Click</a>
     * @param {string} name
     * @param {string} value
     */
    tagProp(propName, propValue) {
        return ` ${propName}="${propValue}"`;
    }

    /**
     * @deprecated
     * Stop all use
     * Will remove in future release.
     * @public @method
        Appends the subpath to the provided url:
        Example:
        url: https://ezclocker.com
        subPath: /public/signin.html
        result: https://ezclocker.com/public/signin.html
     * @param {string} url
     * @param {string} subPath
     */
    urlAppend(url, ...args) {
        let newUrl = url;

        const pathSeparator = EzString.hasLength(url) && url.endsWith('/')
            ? EzString.EMPTY
            : '/';

        for (const argument of args) {
            if (EzString.hasLength(argument)) {
                newUrl = `${newUrl}${pathSeparator}${argument}`;
            }
        }

        return newUrl;
    }

    /**
     * @public @method
     * Builds an api path from the items in the array. Appends the '/' after each item.
     * @param {array} pathParts
     * @deprecated Migrate to different soluction. Removing in a future release
     */
    buildApiPath(pathParts) {
        let apiPath = EzString.EMPTY;

        for (const pathPart of pathParts) {
            apiPath = `${apiPath}${pathPart}/`;
        }

        return apiPath;
    }

    /**
     * @deprecated
        Migreate to EzEventEngine functionality
     * Will remove in future release.
     * @public @method
     * Wraps on event hooks with promise return
     * @param {string} eventName
     */
    ezOn(eventName) {
        if (!EzObject.isValid(eventName)) {
            return null;
        }
        if (EzObject.isValid($)) {
            return EzApi.ezInstance.ezPromise(
                (resolve) => $(window).on(eventName, resolve));
        }

        return EzApi.ezInstance.ezPromise(
            (resolve) => window.addEventListener(eventName, resolve));
    }

    /**
     * @deprecated
     * Migrate to EzUI.ezInstance.ezContent()
     * Will remove in future release.
     * @public @method
     * Uses the jquery.html() function to set the HTML contents of the item associated with the provided id
     * @param {string} id
     * @param {string} html
     */
    ezHtml(id, html) {
        EzApi.ezInstance.ez(id).html(html);
    }

    /**
     * @deprecated
     * Migrate to EzUI.ezInstance.ezContent(EzString.EMPTY)
     * Will remove in future release.
     * @public @method
     * Uses the jquery.html() function to set the HTML contents of the item associated with the provided id
     * @param {string} id
     * @param {string} html
     */
    ezClearHtml(id) {
        EzApi.ezInstance.ez(id).html(EzString.EMPTY);
    }

    /**
     * @deprecated
     * Stop all use
     * Will remove in future release.
     * @public @method
     * Wraps the creation of html via jquery
     * @param {string} htmlString
     */
    newHtml(htmlString) {
        return EzApi.ezInstance.ezIsEmptyArray(htmlString)
            ? null
            : $(htmlString);
    }

    /**
     * @public @method
     * Returns type information from Object.prototype.toString.call(aObject);
     * Result format
        [object {type}]
     * @param {*} aObject
     * @returns {string}
     */
    ezGetObjectType(aObject) {
        return Object.prototype.toString.call(aObject);
    }

    /**
     * @public @method
     * Determines if the provided item is 'undefined'
     * @param {*} item
     * @returns {boolean}
     */
    ezIsUndefined(item) {
        return undefined === item || 'undefined' === typeof item;
    }

    /**
     * @public @method
     * Determines if the provided image is null.
     * @param {*} item
     * @returns {boolean}
     */
    ezIsNull(item) {
        return null == item;
    }

    /**
     * @public @method
     * Determines if the provided item is an object type.
     * @returns {boolean}
     */
    ezIsObject(item) {
        return undefined !== item && null !== item && 'object' === typeof item;
    }

    /**
     * @public @method
     * Determines if the provided item is not null AND not 'undefined'.
     * @param {*} item
     * @returns {boolean}
     */
    ezIsValid(item) {
        return undefined !== item && null !== item && 'undefined' !== typeof item;
    }

    /**
     * @public @method
     * Determines if the provided item is null OR 'undefined'
     * @param {*} item
     * @returns {boolean}
     */
    ezIsNotValid(item) {
        return undefined === item || null === item || 'undefined' === typeof item;
    }

    /**
     * @public @method @method
     * Returns true if ALL the arguments provided to this method are NOT undefined and NOT null.
     * @returns {boolean}
     */
    ezAllValid(...args) {
        for (const argument of args) {
            if (!EzObject.isValid(argument)) {
                return false;
            }
        }
        return true;
    }

    /**
     * @public @method
     * Evaluates all the provided arguments (if any) to be valid
     * @deprecated
     * Use EzApi.ezInstance.ezAllValid(...) instead
     */
    allValid(...args) {
        return EzApi.ezInstance.ezAllValid(...args);
    }

    /**
     * @public @method @method
     * Returns true if any of the argiments provided to this method are NOT undefined or null.
     * @returns {boolean}
     */
    ezAnyValid(...args) {
        for (const argument of args) {
            if (EzObject.isValid(argument)) {
                return true;
            }
        }

        return false;
    }

    /**
     * @public @method
     * Determines if any of the passed arguments are not valid
     * @returns {boolean}
     */
    ezAnyNotValid(...args) {
        for (const aIndex in args) {
            if (!EzObject.isValid(args[aIndex])) {
                return true;
            }
        }

        return false;
    }

    /**
     * @public @method
     * Determines if the provided aObject is a string type
     * @param {*} aObject
     * @returns {boolean}
     */
    ezIsString(aObject) {
        return undefined !== aObject && null !== aObject && 'string' === typeof aObject;
    }

    /**
     * @public @method
     * Returns the provdied aString if it is a valid string. Otherwise, returns an empty string.
     * @param {*} aString
     * @returns {string}
     */
    ezStringOrEmpty(aString) {
        return EzString.stringOrEmpty(aString);
    }

    /**
     * @public @method
     * Returns the provdied aString if it is a valid string. Otherwise, returns null;
     * @param {*} aString
     * @returns {string}
     */
    ezStringOrNull(aString) {
        return EzString.stringOrNull(aString);
    }

    /**
     * @public @method
     * Removes all extract space from the provided aTemplateLitteralValue param. Then returns
        the value with one space in front, once space at end and a line feed.
     * Result template: ' ${value_with_extra_space_removed} \n';
     * @param {string} aTemplateLitteralValue
     * @returns {string}
     */
    ezTemplate(aStrings, ...aKeys) {
        let cleanValues = EzString.EMPTY;

        if (EzApi.ezInstance.ezArrayHasLength(aStrings)) {
            for (let x = 0; x < aStrings.length; x++) {
                let aString = aStrings[x];

                if (EzApi.ezInstance.ezIsNumber(aString) || EzApi.ezInstance.ezIsBoolean(aString)) {
                    aString = aString.toString();
                }

                let aKey = x < aKeys.length
                    ? aKeys[x]
                    : EzString.EMPTY;

                aKey = EzApi.ezInstance.ezIsNumber(aKey) || EzApi.ezInstance.ezIsBoolean(aKey)
                    ? aKey.toString()
                    : EzString.stringOrEmpty(aKey);

                let cleanValue = aString.replace(/( {2})+/g, ' ');

                cleanValue = cleanValue.replace(/\t+/g, ' ');

                cleanValues = `${cleanValues}${cleanValue}${aKey}`;
            }
        }

        return `${cleanValues}`;
    }

    /**
     * @public @method
     * Removes all extract space from the provided aTemplateLitteralValue param. Then returns
        the value with one space in front, once space at end and a line feed.
     * Result template: ' ${value_with_extra_space_removed} \n';
     * @param {string} aTemplateLitteralValue
     * @returns {string}
     */
    ezMessageTemplate(aStrings, ...aKeys) {
        let cleanValues = EzString.EMPTY;

        if (EzApi.ezInstance.ezArrayHasLength(aStrings)) {
            for (let x = 0; x < aStrings.length; x++) {
                let aString = aStrings[x];

                if (EzApi.ezInstance.ezIsNumber(aString) || EzApi.ezInstance.ezIsBoolean(aString)) {
                    aString = aString.toString();
                }

                let aKey = x < aKeys.length
                    ? aKeys[x]
                    : EzString.EMPTY;

                aKey = EzApi.ezInstance.ezIsNumber(aKey) || EzApi.ezInstance.ezIsBoolean(aKey)
                    ? aKey.toString()
                    : EzString.stringOrEmpty(aKey);

                // Remove extra spaces
                let cleanValue = aString.replace(/\t+/g, ' ');

                cleanValue = cleanValue.replace(/( {2})+/g, ' ');

                // Remove line feeds, carrage returns, and tabs
                cleanValue = cleanValue.replace(/[\n,\r]+/g, EzString.EMPTY);

                cleanValues = `${cleanValues}${cleanValue} ${aKey}`;
            }
        }

        return `${cleanValues}`;
    }

    /**
     * @public @method
     * Removes all extract space from the provided aTemplateLitteralValue param. Then returns
        the value with one space in front, once space at end and a line feed.
     * Result template: ' ${value_with_extra_space_removed} \n';
     * @param {string} aTemplateLitteralValue
     * @returns {string}
     */
    ezMsg(aStrings, ...aKeys) {
        let cleanValues = EzString.EMPTY;

        if (EzApi.ezInstance.ezArrayHasLength(aStrings)) {
            for (let x = 0; x < aStrings.length; x++) {
                let aString = aStrings[x];

                if (EzApi.ezInstance.ezIsNumber(aString) || EzApi.ezInstance.ezIsBoolean(aString)) {
                    aString = aString.toString();
                }

                let aKey = x < aKeys.length
                    ? aKeys[x]
                    : EzString.EMPTY;

                aKey = EzObject.isValid(aKey)
                    ? aKey.toString()
                    : EzString.EMPTY;

                // Remove line feeds, carrage returns, and tabs
                let cleanValue = aString.replace(/\n+/g, ' ');

                cleanValue = cleanValue.replace(/[\r\t]+/g, EzString.EMPTY);

                // Remove extra spaces
                cleanValue = cleanValue.replace(/( {2})+/g, ' ');

                const cleanLine = `${cleanValue}${aKey}`;

                cleanValues = `${cleanValues}${cleanLine}`;
            }
        }

        return cleanValues.replace(/( {2})+/g, ' ');
    }

    /**
     * @public @method
     * Removes all extract space from the provided aTemplateLitteralValue param. Then returns
        the value with one space in front, once space at end and a line feed.
     * Result template: ' ${value_with_extra_space_removed} \n';
     * @param {string} aTemplateLitteralValue
     * @returns {string}
     */
    ezEM(aStrings, ...aKeys) {
        return EzApi.ezInstance.ezMsg(aStrings, aKeys);
    }

    /**
     * @public @method
     * Removes all spaces and line feeds from the provided aTemplateLitteralValue param. Then returns
        the trimmed value.
     * Result template: '${value_with_no_space_linefeeds}';
     * @param {string} aTemplateLitteralValue
     * @returns {string}
     */
    ezUrlTemplate(aStrings, ...aKeys) {
        let cleanValues = EzString.EMPTY;

        if (EzApi.ezInstance.ezArrayHasLength(aStrings)) {
            for (let x = 0; x < aStrings.length; x++) {
                let aString = aStrings[x];

                if (EzApi.ezInstance.ezIsNumber(aString) || EzApi.ezInstance.ezIsBoolean(aString)) {
                    aString = aString.toString();
                }

                aString = EzString.hasLength(aString)
                    ? aString.trim()
                    : EzString.EMPTY;

                let cleanString = aString.replace(/[ \t\n\r]+/g, EzString.EMPTY);

                if (x < aKeys.length) {
                    let aKey = EzApi.ezInstance.ezIsString(aKeys[x])
                        ? aKeys[x].trim()
                        : EzApi.ezInstance.ezAssignOrDefault(aKeys[x], EzString.EMPTY);

                    aKey = EzApi.ezInstance.ezIsNumber(aKey) || EzApi.ezInstance.ezIsBoolean(aKey)
                        ? aKey.toString()
                        : EzString.stringOrEmpty(aKey);

                    const cleanKey = aKey.replace(/[ \t\n\r]+/g, EzString.EMPTY);

                    cleanString = `${cleanString}${cleanKey}`.trim();
                }

                cleanValues = `${cleanValues}${cleanString}`.trim();
            }
        }

        return `${cleanValues}`;
    }

    /**
     * @public @method
     * Removes all spaces and line feeds from the provided aTemplateLitteralValue param. Then returns
        the trimmed value.
     * Result template: '${value_with_no_space_linefeeds}';
     * @param {string} aTemplateLitteralValue
     * @returns {string}
     */
    ezSingleLineTemplate(aStrings, ...aKeys) {
        let cleanValues = EzString.EMPTY;

        if (EzApi.ezInstance.ezArrayHasLength(aStrings)) {
            for (let x = 0; x < aStrings.length; x++) {
                let aString = aStrings[x];

                if (EzApi.ezInstance.ezIsNumber(aString) || EzApi.ezInstance.ezIsBoolean(aString)) {
                    aString = aString.toString();
                }

                aString = EzString.hasLength(aString)
                    ? aString.trim()
                    : EzString.EMPTY;

                let aKey = x < aKeys.length
                    ? aKeys[x]
                    : EzString.EMPTY;

                aKey = EzApi.ezInstance.ezIsNumber(aKey) || EzApi.ezInstance.ezIsBoolean(aKey)
                    ? aKey.toString()
                    : EzString.stringOrEmpty(aKey);

                const cleanValue = aString.trimStart().replace(/[ \n\r\t]+/g, EzString.EMPTY);

                const cleanLine = `${cleanValue}${aKey}`.trim();

                cleanValues = `${cleanValues}${cleanLine}`.trim();
            }
        }

        return `${cleanValues}`.trim();
    }

    /**
     * @public @method
     * Removes all spaces and line feeds from the provided aTemplateLitteralValue param. Then returns
     * the trimmed value.
     * Result template: '${value_with_no_space_linefeeds}';
     * @param {string} aTemplateLitteralValue
     * @returns {string}
     */
    ezIdTemplate(aStrings, ...aKeys) {
        let cleanValues = EzString.EMPTY;

        if (EzApi.ezInstance.ezArrayHasLength(aStrings)) {
            for (let x = 0; x < aStrings.length; x++) {
                let aString = aStrings[x];

                if (EzApi.ezInstance.ezIsNumber(aString) || EzApi.ezInstance.ezIsBoolean(aString)) {
                    aString = aString.toString();
                }

                aString = EzString.hasLength(aString)
                    ? aString.trim()
                    : EzString.EMPTY;

                let aKey = x < aKeys.length
                    ? aKeys[x]
                    : EzString.EMPTY;

                aKey = EzApi.ezInstance.ezIsNumber(aKey) || EzApi.ezInstance.ezIsBoolean(aKey)
                    ? aKey.toString()
                    : EzString.stringOrEmpty(aKey);

                const cleanValue = aString.trimStart().replace(/[ \n\r\t]+/g, EzString.EMPTY);

                const cleanLine = `${cleanValue}${aKey}`.trim();

                cleanValues = `${cleanValues}${cleanLine}`.trim();
            }
        }

        return `${cleanValues}`.trim();
    }

    /**
     * @public @method
     * Returns a JSON string.
     * @param {array} aStrings
     * @param {array} akeys
     * @returns {string}
     */
    ezJsonTemplate(aStrings, ...aKeys) {
        let cleanValues = EzString.EMPTY;

        if (EzApi.ezInstance.ezArrayHasLength(aStrings)) {
            for (let x = 0; x < aStrings.length; x++) {
                let aString = aStrings[x];

                if (EzApi.ezInstance.ezIsNumber(aString) || EzApi.ezInstance.ezIsBoolean(aString)) {
                    aString = aString.toString();
                }

                aString = EzString.hasLength(aString)
                    ? aString
                        .trim()
                        .replaceAll('  ', EzString.EMPTY)
                    : EzString.EMPTY;

                let aKey = x < aKeys.length
                    ? aKeys[x]
                    : EzString.EMPTY;

                aKey = EzApi.ezInstance.ezIsNumber(aKey) || EzApi.ezInstance.ezIsBoolean(aKey)
                    ? aKey.toString()
                    : EzString.stringOrEmpty(aKey);

                aKey = aKey.trim();

                const cleanValue = aString.replace(/[\n\r\t]+/g, EzString.EMPTY);

                const cleanLine = `${cleanValue}${aKey}`;

                cleanValues = `${cleanValues}${cleanLine}`;
            }
        }

        return `${cleanValues}`;
    }

    /**
     * @public @method
     * Returns the provdied aString if it is a valid string. Otherwise, returns an empty string.
     * @param {*} aString
     * @returns {string}
     */
    ezStringOrDefault(aString, aDefault) {
        return undefined !== aString && null !== aString && 'string' === typeof aString
            ? aString
            : aDefault;
    }

    /**
     * @public @method
     * Determines if the value passed is a string
     * @param {*} value
     * @returns {boolean}
     * @deprecated Migrate to EzApi.ezInstance.ezIsString()
     */
    isStringType(aObject) {
        return EzApi.ezInstance.ezIsString(aObject);
    }

    /**
     * @public @method
     * Determines if the passed object is an actual moment.js object or not.
     * @param {*} aObject
     * @returns {boolean}
     */
    ezIsMoment(aObject) {
        return undefined !== aObject && null !== aObject && 'undefined' !== typeof object && moment(aObject).isValid();
    }

    /**
     * @public @method
     * Determines if the passed array is not undefined, not null, and has a length greater than zero
     * @param {*} aArray
     * @returns {boolean}
     */
    ezIsEmptyArray(aArray) {
        return undefined === aArray || null === aArray ||
            ('[object Array]' === EzApi.ezInstance.ezGetObjectType(aArray) && 0 === aArray.length);
    }

    /**
     * @public @method
     * Determines if the passed array is not undefined, not null, and has a length greater than zero
     * @param {*} aArray
     * @returns {boolean}
     */
    ezIsNotArrayOrEmptyArray(aArray) {
        return undefined === aArray || null === aArray ||
            '[object Array]' !== EzApi.ezInstance.ezGetObjectType(aArray) ||
            0 === aArray.length;
    }

    /**
     * @public @method
     * Determines if the passed array is not undefined, not null, and has a length greater than zero
     * @param {*} aArray
     * @returns {boolean}
     */
    ezIsNotEmptyArray(aArray) {
        return undefined !== aArray && null !== aArray &&
            '[object Array]' === EzApi.ezInstance.ezGetObjectType(aArray) && 0 !== aArray.length;
    }

    /**
     * @public @method
     * Determines if the passed aArray is not undefined, not null, and has a length greater than zero
     * @param {*} aArray
     * @returns {boolean}
     */
    ezArrayHasLength(aArray) {
        return undefined !== aArray && null !== aArray &&
            '[object Array]' === EzApi.ezInstance.ezGetObjectType(aArray) && 0 !== aArray.length;
    }

    /**
     * @public @method
     * Determines if the passed object is an array or not.
     * @param {*} aArray
     * @returns {boolean}
     */
    ezIsArray(aArray) {
        return undefined !== aArray && null !== aArray && '[object Array]' === EzApi.ezInstance.ezGetObjectType(aArray);
    }

    /**
     * @public @method
     * Returns the provided aArray if it is a valid array. Otherwise, returns an empty array.
     * @param {*} aArray
     * @returns {array}
     */
    ezArrayOrEmpty(aArray) {
        return undefined !== aArray && null !== aArray && '[object Array]' === EzApi.ezInstance.ezGetObjectType(aArray)
            ? aArray
            : [];
    }

    /**
     * @public @method
     * Determines if the passed object is a number type.
     * @param {*} aObject
     * @returns {boolean}
     */
    ezIsNumber(aObject) {
        return undefined !== aObject && null !== aObject && 'number' === typeof aObject;
    }

    /**
     * @public @method
     * Returns the provided aNumber if it is a valid number, Otherwise, returns null
     * @param {*} aNumber
     * @returns {Number|null}
     */
    ezNumberOrNull(aNumber) {
        return undefined !== aNumber && null !== aNumber &&
            ('number' === typeof aNumber || '[object Number]' === EzApi.ezInstance.ezGetObjectType(aNumber))
            ? aNumber
            : null;
    }

    /**
     * @public @method
     * Returns the provided aNumber if it is a valid number, Otherwise, returns the provided aDefault value.
     * @param {*} aNumber
     * @param {Number|null} aDefault
     * @returns {Number|{aDefault}}
     */
    ezNumberOrDefault(aNumber, aDefault) {
        return undefined !== aNumber && null !== aNumber &&
            ('number' === typeof aNumber || '[object Number]' === EzApi.ezInstance.ezGetObjectType(aNumber))
            ? aNumber
            : aDefault;
    }

    /**
     * @public @method
     * Determines if the passed object is NOT a number type.
     * @param {*} aObject
     */
    ezIsNotNumber(aObject) {
        return undefined === aObject && null === aObject ||
            ('number' !== typeof aObject && '[object Number]' !== EzApi.ezInstance.ezGetObjectType(aObject));
    }

    /**
     * @public @method
     * Converts a string value to a Javascript number (using parseInt)
     * @param {string} stringValue
     * @param {number} [radix]
        Optional radix value for parseInt
     * @returns {number}
     * Note that if the value cannot be converted, NaN is returned.
     */
    ezToNumber(stringValue, radix) {
        if (!EzString.hasLength(stringValue)) {
            // fall to zero if the string value is null, empty, or undefined
            return 0;
        }

        try {
            return parseInt(stringValue, radix);
        } catch (err) {
            return null;
        }
    }

    /**
     * @public @method
     * Converts a string value to a Javascript number (using parseInt)
     * @param {string} stringValue
     * @param {number} [radix]
        Optional radix value for parseInt
     * @returns {number}
     * Note that if the value cannot be converted, NaN is returned.
     */
    ezToFloat(stringValue) {
        if (!EzString.hasLength(stringValue)) {
            // fall to zero if the string value is null, empty, or undefined
            return 0.00;
        }

        try {
            return parseFloat(stringValue);
        } catch (err) {
            return null;
        }
    }

    /**
     * @public @method
     * Determines if the provided aNumber param is within the given range.
     * If aNumber is not a valid number, false is returned.
     * If aMinNumber is not provided, it is assumed equal to aNumber.
     * If aMaxNumber is not provided, it is assumed equal to aNumber.
     * If aMinNumber is greater than aMaxNumber, throws exception
     * If aMaxNumber is less than a aMinNumber, throws exception
     * @param {number} aNumber
     * @param {number} aMinNumber
     * @param {number} aMaxNumber
     * @returns {boolean}
     * Returns true if the following is true:
     * 1) aNumber is >= aMinNumber
     * 2) aNumber <= aMaxNumber
     */
    ezNumberWithinRange(aNumber, aMinNumber, aMaxNumber) {
        if (aMinNumber > aMaxNumber) {
            throw new EzBadParamException('aMinNumber', 'EzApi', 'ezNumberBetweenRange');
        }

        if (undefined === aNumber || null === aNumber ||
            ('number' !== typeof aNumber && '[object Number]' !== EzApi.ezInstance.ezGetObjectType(aNumber))) {
            // Not a number
            return false;
        }

        return aNumber >= EzApi.ezInstance.ezNumberOrDefault(aMinNumber, aNumber) &&
            aNumber <= EzApi.ezInstance.ezNumberOrDefault(aMaxNumber, aNumber);
    }

    /**
     * @public @method
     * Determines if the provided aNumber param is between the aStartNumber and aEndNumber range.
     * If aNumber is not a valid number, false is returned.
     * If aEndNumber is not provided, it is assumed equal to aNumber - 1.
     * If aStartNumber is not provided, it is assumed equal to aNumber + 1.
     * If aStartNumber is greater than aEndNumber, throws exception
     * If aEndNumber is less than a aStartNumber, throws exception
     * @param {number} aNumber
     * @param {number} aStartNumber
     * @param {number} aEndNumber
     * @returns {boolean}
     * Returns true if the following is true:
     * 1) aNumber is > aStartNumber
     * 2) aNumber is < aEndNumber
     */
    ezNumberBetweenRange(aNumber, aStartNumber, aEndNumber) {
        if (undefined === aStartNumber || null === aStartNumber) {
            throw new EzBadParamException('aStartNumber', 'EzApi', 'ezNumberBetweenRange');
        }
        if (undefined === aEndNumber || null == aEndNumber) {
            aEndNumber = aStartNumber;
        }
        if (aStartNumber > aEndNumber) {
            throw new EzBadParamException('aStartNumber', 'EzApi', 'ezNumberBetweenRange');
        }

        if (undefined === aNumber || null === aNumber ||
            ('number' !== typeof aNumber && '[object Number]' !== EzApi.ezInstance.ezGetObjectType(aNumber))) {
            // Not a number
            return false;
        }

        return aNumber > EzApi.ezInstance.ezNumberOrDefault(aStartNumber, aNumber - 1) &&
            aNumber < EzApi.ezInstance.ezNumberOrDefault(aEndNumber, aNumber + 1);
    }

    /**
     * @public @method
     * Determines if the passed object is a boolean type.
     * @param {*} aObject
     * @returns {boolean}
     */
    ezIsBoolean(aObject) {
        return undefined !== aObject && null !== aObject &&
            ('boolean' === typeof aObject || '[object Boolean]' === EzApi.ezInstance.ezGetObjectType(aObject));
    }

    /**
     * @public @method
     * Determines if the passed object is NOT boolean type.
     * @param {*} aObject
     * @returns {boolean}
     */
    ezIsNotBoolean(aObject) {
        return undefined === aObject || null === aObject ||
            ('boolean' !== typeof aObject && '[object Boolean]' !== EzApi.ezInstance.ezGetObjectType(aObject));
    }

    /**
     * @public @method
     * Returns true if the boolean value is false. Otherwise, returns false if the value is NOT a boolean at all OR
        is actually true.
     * @param {boolean} aValue
     * @returns {boolean}
     */
    ezIsFalse(aValue) {
        return undefined !== aValue && null !== aValue &&
            ('boolean' === typeof aValue || '[object Boolean]' === EzApi.ezInstance.ezGetObjectType(aValue)) &&
            false === aValue;
    }

    /**
     * @public @method
     * Returns true if the boolean value is true. Otherwise, returns false if the value is NOT a boolean at all OR
        is actually false.
     * @param {boolean} aValue
     * @returns {boolean}
     * @deprecated Migrate to EzBoolean.isTrue()
     */
    isTrue(aValue) {
        return EzBoolean.isTrue(aValue);
    }

    /**
     * @public @method
     * Returns true if the boolean value is true. Otherwise, returns false if the value is NOT a boolean at all OR
        is actually false.
     * @param {boolean} aValue
     * @returns {boolean}
     */
    ezIsTrue(aValue) {
        return undefined !== aValue && null !== aValue &&
            ('boolean' === typeof aValue || '[object Boolean]' === EzApi.ezInstance.ezGetObjectType(aValue)) &&
            true === aValue;
    }

    /**
     * @public @method
     * Returns true if the passed ref is a javascript function
     * @param {Function} aFunction
     * @returns {boolean}
     */
    ezIsFunction(aFunction) {
        return undefined !== aFunction && null !== aFunction &&
            ('function' === typeof aFunction || '[object Function]' === EzApi.ezInstance.ezGetObjectType(aFunction));
    }

    /**
     * @public @method
     * Returns true if the passed ref is a javascript function
     * @param {Function} aFunction
     * @returns {boolean}
     */
    ezIsNotFunction(aFunction) {
        return undefined !== aFunction && null === aFunction ||
            ('function' !== typeof aFunction && '[object Function]' !== EzApi.ezInstance.ezGetObjectType(aFunction));
    }

    /**
     * @public @method
     * Returns the provided item for assignment to a let if the item is valid. Otherwise, the defaultItem is returned for
        assignment.
     * @param {*} aItem
     * @param {*} aDefaultValue
     * @returns {*}
     * @deprecated Migrate to EzApi.ezInstance.ezAssignOrDefault()
     */
    ezReturnValidOrDefault(aItem, aDefaultValue) {
        return undefined !== aItem && null !== aItem && 'undefined' !== typeof aItem
            ? aItem
            : aDefaultValue;
    }

    /**
     * @public @method
     * Returns the provided value if it is a valid string, otherwise EzString.EMPTY string is returned
     * @param {string} aValue
     * @returns {string}
     * @deprecated Migrate to EzString.stringOrEmpty()
     */
    ezReturnValidOrEmptyString(aValue) {
        return EzString.stringOrEmpty(aValue);
    }

    /**
     * @public @method
     * Returns the provided item to assign to a variable if the item is valid. Otherwise, null is returned
     * @param {*} item
     * @returns {*|null}
     * @deprecated Migrate to EzApi.ezInstance.ezAssignOrNull()
     */
    ezReturnValidOrNull(item) {
        return EzApi.ezInstance.ezAssignOrNull(item);
    }

    /**
     * @public @method
     * Determines if the passed aValue is undefined, null, or has length zero
     * @param {*} aValue
     * @returns {boolean}
     * @deprecated
     * Migrate to: !EzString.hasLength(aValue)
     */
    ezIsEmptyString(aValue) {
        return !EzString.hasLength(aValue);
    }

    /**
     * @public @method
     * Determines if the passed aValue is a string type AND the length is greater than zero.
     * @param {*} aValue
     * @returns {boolean}
     * @deprecated
     * Migrate to: EzString.hasLength(aValue)
     */
    ezStringHasLength(aValue) {
        return EzString.hasLength(aValue);
    }

    /**
     * @public @method
     * Determines if the passed aValue is a string type AND the length is greater than zero.
     * @param {*} aValue
     * @returns {boolean}
     * @deprecated Migrate to EzString.hasLength(aValue)
     */
    ezIsNotEmptyString(aValue) {
        return EzString.hasLength(aValue);
    }

    /**
     * @public @method
     * Returns the aValue as a string.
     * Converts numbers to strings.
     * Returns objects as JSON
     * Returns null/undefined as aDefaultValue
     * @param {*} aValue
     * @param {string|null} aDefaultValue
     * @returns {string}
     */
    ezString(aValue, aDefaultValue) {
        if (undefined === aValue || null === aValue || 'undefined' === typeof aValue) {
            return aDefaultValue;
        }

        const stringValue = EzNumber.isNumber(aValue)
            ? EzNumber.asString(aValue)
            : aValue;

        return EzString.isString(aValue)
            ? aValue
            : EzJson.toJson(stringValue);
    }

    /**
     * @public @method
     * Executes Promise.reject passing the provided response.
     * If the response is a string, an error is also logged.
     * @param {object} response
     * @returns {Promise.reject}
     */
    ezReject(response) {
        if (!EzObject.isValid(response)) {
            return EzPromise.reject({
                errorCode: 500,
                message: 'An operation encountered an unexpected error. No additional details available.'
            });
        }

        if (EzString.hasLength(response)) {
            return EzPromise.reject({
                errorCode: 500,
                message: response
            });
        }

        return EzPromise.reject(response);
    }

    /**
     * @public @method
     * Executes Promise.reject passing the provided response.
     * @param {string|null} em
     * @returns {Promise.reject}
     * @deprecated Migrate to EzApi.ezInstance.ezReject
     */
    ezRejectWithError(em) {
        return EzString.hasLength(em)
            ? Promise.reject(em)
            : Promise.reject({
                errorCode: 500,
                message: 'An operation encountered an unexpected error.'
            });
    }

    /**
     * @public @method
     * Executes Promise.resolve, passing the provided response in the
        resolve.
     * @param {object} response
     * @returns {Promise.resolve}
     */
    ezResolve(response) {
        return Promise.resolve(response);
    }

    /**
     * @public @method
     * Wraps the provided function in a promise (only expecting a RESOLVE result)
     * @param {Function} promiseFunction
     * @returns {Promise.resolve}
     */
    ezResolver(promiseFunction) {
        return !EzFunction.isFunction(promiseFunction)
            ? EzPromise.resolve(promiseFunction)
            : EzPromise.asyncAction(
                (resolve) => EzPromise.promise(promiseFunction)
                    .then(resolve, resolve)
                    .catch(EzApi.ezInstance.ezHandlePromiseCatch))
                .catch(EzApi.ezInstance.ezHandlePromiseCatch);
    }

    /**
     * @public @method
     * Alternative to a resolve only promise (or EzApi.ezInstance.ezResolver) to help communicate what is going on.
     * @param {Function) aFunction
     */
    ezAsyncAction(aFunction) {
        return EzPromise.asyncAction(aFunction);
    }

    /**
     * @public @method @method
     * Returns the result of wrapping the provided function in Promise.resolve()
     * @returns {Promise.resolve}
     */
    ezAsyncOperation(aFunction) {
        if (!EzFunction.isFunction(aFunction)) {
            throw new EzBadParamException(
                'aFunction',
                EzApi.ezInstance,
                EzApi.ezInstance.ezAsyncOperation);
        }

        return new Promise(
            (finished) => {
                aFunction();

                return finished();
            });
    }

    /**
     * @public @method
     * Immediatly returns the 'Promise.resolve'
     * @returns {*}
     */
    ezFinished(response) {
        return Promise.resolve(response);
    }

    /**
     * @public @method
     * Wraps the provided function in a promise (only expecting a RESOLVE result)
     * @param {Function} promiseFunction
     * @returns {Promise.resolve}
     */
    ezRejector(promiseFunction) {
        return !EzFunction.isFunction(promiseFunction)
            ? EzPromise.reject(promiseFunction)
            : EzPromise.promise(
                (resolve, reject) => {
                    if (null != resolve) {
                        resolve = reject;
                    }

                    return EzPromise.promise(promiseFunction)
                        .then(resolve, reject)
                        .catch(reject);
                });
    }

    /**
     * @public @method
     * Wraps the creation of a promise into the ezApi
     * @param {Function} aFunction
     */
    ezPromise(aFunction) {
        if (!EzFunction.isFunction(aFunction)) {
            throw new EzBadParamException(
                'aFunction',
                EzApi.ezInstance,
                EzApi.ezInstance.ezPromise);
        }

        return new Promise(aFunction)
            .catch(EzApi.ezInstance.ezHandlePromiseCatch);
    }

    /**
     * @public @method
     * Handles a promise.catch
     * @param {object} err
     */
    ezHandlePromiseCatch(err) {
        const em = 'EzApi.ezPromise execution failed with unhandled exception. ';

        if (EzObject.isValid(err)) {
            if (EzApi.ezInstance.ezIsNumber(err.errorCode)) {
                // Should get handled in the reject call
                EzLogger.error(
                    EzString.em`
                        ${em}
                        Error: ${EzJson.toJson(err)}`);
            } else {
                EzLogger.error(
                    EzString.em`
                        ${em}
                        Error: ${err.message}`);
            }
        } else {
            EzLogger.ezLogException(em, err);
        }

        //return promiseError;
        throw (err);
    }

    /**
     * @public @method
     * Provides the method for handling promise rejects with zero processing.
     */
    ezIgnoreReject() {
        // promise rejection handler, does nothing
    }

    /**
     * @public @method
     * Provides the method for handling resolves with zero processing.
     */
    ezIgnoreResolve() {
        // promise resolve handler, does nothing
    }
    /**
     * @public @method
     * Provides a method for ignoring Promise.catch calls.
     */
    ezIgnoreCatch() {
        // Promise catch handler that does nothing
    }

    /**
     * @public @method
     * Trims the provided value or returns null if not a valid string
     * @param {*} value
     */
    trimEmptyStringToNull(value) {
        return EzString.hasLength(value)
            ? value.trim()
            : null;
    }

    /**
     * @public @method
     * Applies URL decoding to the provided string
     * @param {string} value
     */
    ezDecode(value, replacePlusWithSpace) {
        if (!EzString.hasLength(value)) {
            return value;
        }

        let decodedValue = decodeURIComponent(value);

        if (EzBoolean.isTrue(replacePlusWithSpace)) {
            decodedValue = decodedValue.replaceAll('+', ' ');
        }

        return decodedValue;
    }

    /**
     * @public @method
     * Applies URL encoding to the provided string
     * @param {string} value
     */
    ezEncode(value) {
        if (!EzString.hasLength(value)) {
            return value;
        }

        return encodeURIComponent(value).replace(
            /[!'()*]/g,
            (c) => `%${c.charCodeAt(0).toString(16)}`);
    }

    /**
     * @public @method
     * Replaces HTML special character with their HTML codes
     * @param {string} value
     * @returns {string}
     * @deprecated
     * Migrate to: EzHtml.encodeHtml(value)
     */
    ezHtmlEncode(value) {
        return EzHtml.encodeHtml(value);
    }

    /**
     * @public @method
     * Converts the provided number to a string or returns NAN if not possible
     * @param {number} num
     * @returns {string}
     */
    ezNumberToString(num) {
        if (!EzApi.ezInstance.ezIsNumber(num)) {
            // If it is a string, return that. Otherwise, return NAN
            return EzString.hasLength(num)
                ? num
                : 'NAN';
        }

        try {
            return num.toString();
        } catch (ex) {
            return 'NAN';
        }
    }

    /**
     * @public @method
     * Converts the provided number to a string. If conversion fails, either the defaultValue
     * is returned (if not null/empty string) OR 'NAN' is returned.
     * @param {number} num,
     * @param {null|string} defaultValue
     * @returns {string}
     */
    ezNumberToStringDefault(num, defaultValue) {
        defaultValue = !EzString.hasLength(defaultValue)
            ? 'NAN'
            : defaultValue;

        if (!EzApi.ezInstance.ezIsNumber(num)) {
            // If it is a string, return that. Otherwise, return defaultValue
            return EzString.hasLength(num) ? num : defaultValue;
        }

        try {
            return num.toString();
        } catch (ex) {
            return defaultValue;
        }
    }

    /**
     * @public @method
     * Safely extracts a value from an array. Returns the provided defaultValue if unable to obtain a value from
     * the provided array at the provided index OR returns null if defaultValue is not provided.
     * @param {array} array
     * @param {number} index
     * @param {*} defaultValue
     */
    aGet(array, index, defaultValue) {
        defaultValue = EzObject.isValid(defaultValue)
            ? defaultValue
            : null;

        if (EzObject.isValid(index) && EzApi.ezInstance.ezArrayHasLength(array) && array.length > index) {
            const item = array[index];

            return EzObject.isValid(item)
                ? item
                : defaultValue;
        }

        return defaultValue;
    }

    /**
     * @public @method
     * Logs a deprecation message
     * @param {string} deprecatedItem
     * @param {string} useInsteadItem
     * @param {string} additionalMessage
     */
    deprecated(deprecatedItem, useInsteadItem, additionalMessage) {
        if (!EzString.hasLength(deprecatedItem)) {
            // not logging, no specific item
            return;
        }

        let dm = `DEPRECATED: ${deprecatedItem} is deprecated and will be removed in the future.`;

        if (EzString.hasLength(useInsteadItem)) {
            dm = `${dm} Please use ${useInsteadItem} instead.`;
        }

        if (EzString.hasLength(additionalMessage)) {
            dm = `${dm} ${EzString.stringWithLengthOrEmpty(additionalMessage)}`;
        }

        if (EzObject.isValid(ezLogger)) {
            EzLogger.warn(dm);
        } else {
            window.console.warn(dm);
        }
    }

    /**
     * @public @method
     * Opens a new window in the browser to the provided url and adds the provided windowTitle. If not title is provided
     * ezclocker.com is used instead.
     * @param {string} url
     * @param {string} windowTitle
     */
    ezNewWindow(url, windowTitle) {
        if (EzString.hasLength(url)) {
            window.open(
                url,
                !EzString.hasLength(windowTitle)
                    ? 'ezClocker.com'
                    : windowTitle);
        }
    }

    /**
     * @public @method
     * Implementation of onCallback handler. All arguments (except for the callback function reference) are
     * passed into the onCallback function call.
     * @param {Function} callback
     * @returns {Promise.resolve}
     * Resolve contains boolean to determine processing can proceed, false if processing should stop
     */
    ezOnCallback(...args) {
        const callBack = EzArray.hasLength(args)
            ? args[0]
            : null;

        if (!EzFunction.isFunction(callBack)) {
            // no handler, continue
            return EzApi.ezInstance.ezResolve(true);
        }

        return EzPromise.promise(
            (resolve, reject) => {
                return EzBoolean.booleanOrFalse(
                    callBack.apply(this, args.slice(1)))
                    ? reject(false)
                    : resolve(true);
            });
    }

    /**
     * @public @method
     * Verifies the passed function is valid, and returns the call to that method. Otherwise, returns null
     */
    ezCallback(...args) {
        const callBack = EzArray.hasLength(args)
            ? args[0]
            : null;

        if (!EzFunction.isFunction(callBack)) {
            return null;
        }

        return callBack.apply(this, args.slice(1));
    }

    /**
     * @public @method
     * Returns the function for assignment, or returns null if not a valid function.
     * @param {Function|null} handler
     * @deprecated Use a Promise pattern instead.
     */
    ezAssignHandler(handler) {
        return EzFunction.isFunction(handler)
            ? handler
            : function() {
            };
    }

    /**
     * @public @method
     * Creates a basic JSON service response object
     * @param {long|null} errorCode
     * @param {string|null} message
     * @returns {object}
     */
    ezResponse(errorCode, message) {
        return {
            errorCode: EzObject.isValid(errorCode)
                ? errorCode
                : 0,
            message: EzString.hasLength(message)
                ? message
                : 'Success'
        };
    }

    /**
     * @public @method
     * Returns if the current browser is a variation of Internet Explorer or Edge
     * @returns {boolean}
     * @deprecated Use ezlibrary/ez-old-browser-check.js functionality instead.
     */
    ezIsIeCrap() {
        const ua = window.navigator.userAgent;

        // Test values; Uncomment to check result …
        // IE 10
        // ua = 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)';
        // IE 11
        // ua = 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko';
        // Edge 12 (Spartan)
        // ua = 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/39.0.2171.71 Safari/537.36 Edge/12.0';
        // Edge 13
        // ua = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/46.0.2486.0 Safari/537.36 Edge/13.10586';
        const msie = ua.indexOf('MSIE ');

        if (msie > 0) {
            // IE 10 or older => return version number
            return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
        }

        const trident = ua.indexOf('Trident/');

        if (trident > 0) {
            // IE 11 => return version number
            const rv = ua.indexOf('rv:');

            return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
        }

        const edge = ua.indexOf('Edge/');

        if (edge > 0) {
            // Edge (IE 12+) => return version number
            return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
        }

        // other browser
        return false;
    }

    /**
     * @public @method
     * Injects the Internet Explorer Outdated Message if the browser is a flavor of Internet Explorer or Edge.
     */
    ezInjectIeWarning() {
        if (EzApi.ezInstance.ezIsFalse(EzApi.ezInstance.ezIsIeCrap())) {
            return;
        }

        // Internet Explorer Warning
        EzApi.ezInstance.ezId('_PageContent').prepend('<div style="padding:4px;background-color:#ffc500">' +
            '<h1>Supported browsers</h1>' +
            '<p>We design ezClocker.com to support the latest web browsers. We support the current versions of Chrome, ' +
            'Firefox, Safari, and Microsoft Edge.</p>' +
            '<h1>Extended Support Releases</h1>' +
            '<p>While we do our best to support a browser provider\'s extended support releases, older version of a ' +
            'browser might cause some aspects of ezClocker.com to stop working. In such cases, enabling the functionality ' +
            'will require the browser\'s latest release.</p>' +
            '<h1>Beta and Other Special Builds</h1>' +
            '<p>Any browser that is not the officially latet release version might have defects that will prevent ezClocker.com from '
            +
            'functioning as expected. If you encounter such a defects, please reach out to the browser provider\'s support team as '
            +
            'ezClocker.com cannot provide a solution.</p></div>');
    }

    /**
     * @public @method
     * Returns the provided selfToUse if the reference is valid. Otherwise, returns the instance referenced by the provided
     * apiName. *
     * @param {string} apiName
     * @param {Object|null} selfToUse
     * @returns {object}
     */
    ezSelf(apiName, selfToUse) {
        let theApi = EzObject.isValid(selfToUse) ? selfToUse : EzApi.ezInstance.ezclocker[apiName];

        if (!EzObject.isValid(theApi)) {
            // attempt to pull from global
            theApi = window[apiName];
        }

        if (!EzObject.isValid(theApi)) {
            EzApi.ezInstance.ezLogError('Unable to locate an ezApi with the name ' + apiName);
            return null;
        }

        return theApi;
    }

    /**
     * @public @method
     * Applies the the / character as the last charcter of the provided path.
     * If the path already has / as the last charcter, the path is returned as is.
     * If the path is empty or null, a single / character is returned
     * @param {string|null} path
     * @returns {string}
     * A string value of the path with a single applied / at the end if needed.
     */
    ezPostSlash(path) {
        if (!EzString.hasLength(path)) {
            return '/';
        }

        return path[path.length - 1] === '/'
            ? path
            : `${path}/`;
    }

    /**
     * @public @method
     * Applies the the / character as the first charcter of the provided path.
     * If the path already has / as the first charcter, the path is returned as is.
     * If the path is empty or null, a single / character is returned
     * @param {string|null} path
     * @returns {string}
     * A string value of the path with a single / at the beginning if needed.
     */
    ezPreSlash(path) {
        if (!EzString.hasLength(path)) {
            return '/';
        }

        return path[0] === '/'
            ? path
            : `/${path}`;
    }

    /**
     * @public @method
     * Builds a path from the provided arguments. Placeing the / character at the
     * beginning (if not already there), between each argument (if not already there).
     * A / character is NOT added to the end of the path, but if one exists in the last argument it will return.
     * @param {arguments}
     * @returns {string}
     * A string value of the host root relative path
     */
    ezBuildRootPath(...args) {
        if (!EzArray.hasLength(args)) {
            return '/';
        }

        const path = EzApi.ezInstance.isArray(args[0]) ?
            EzApi.ezInstance.ezBuildUrl(EzString.EMPTY, args[0]) :
            EzApi.ezInstance.ezBuildUrl.call(this, EzString.EMPTY, args);

        return path.startsWith('/')
            ? path.substring(1)
            : path;
    }

    /**
     * @public @method
     * Builds a url from the baseUrl and array of path parts.
     * Builds baseUrl is used as the initial url. Any / character at the end of baseUrl is removed first.
     * Path parts are appended to the initial url with a preceeding / character (if one doesn't already exist).
     * @param {string|null} baseUrl
     * Builds path's prefixing domain + host (if any)
     * Defualt: '/'
     * @param {array|null} pathParts
     * Builds array of paths to build
     * Default: []
     * @returns {string}
     * A string value of the host root relative path
     */
    ezBuildUrl(baseUrl = '/', pathParts = []) {
        let path = EzUrl.postSlash(
            EzString.stringWithLengthOrEmpty(baseUrl));

        if (!EzArray.hasLength(pathParts)) {
            return path;
        }

        for (const pathPart of pathParts) {
            if (EzString.hasLength(pathPart)) {
                // Appending to path: {arguments[ai]}/
                if (path.endsWith('/') && pathPart.startsWith('/')) {
                    path = `${path}${pathPart.substr(1)}`;
                } else if (!path.endsWith('/') && !pathPart.startsWith('/')) {
                    path = `${path}/${pathPart}`;
                } else {
                    path = `${path}${pathPart}`;
                }
            }
        }

        return path;
    }

    /**
     * @public @method
     * Creates new references of objects based upon the ezClocker JS Object template.
     * @param {Function} ezClockerApiObject
     * @returns {object}
     */
    ezNew(...args) {
        const constructorFunction = EzArray.hasLength(args)
            ? args[0]
            : null;

        if (!EzObject.isValid(constructorFunction) || !EzFunction.isFunction(constructorFunction)) {
            EzApi.ezInstance.ezLogError('ezNew requires the first argument to be the object\'s constructor function.');

            return null;
        }

        const ref = new (
            Function.prototype.bind.apply(constructorFunction, args));

        return EzFunction.isFunction(ref.ezInit)
            ? ref.ezInit()
            : ref;
    }

    /**
     * @public @method
     * Returns true if the response contains the basic pieces: Error Code and message, neither being null
     * @param {} response
     */
    ezIsValidApiResponse(response) {
        return EzObject.isValid(response) &&
            EzObject.isValid(response.errorCode) &&
            EzObject.isValid(response.message);
    }

    /**
     * @public @method
     * Returns true if the response is a valid api response AND the error code is not zero.
     * @param {*} eResponse
     */
    ezIsValidApiErrorResponse(eResponse) {
        return EzObject.isValidApiResponse(eResponse) && eResponse.errorCode !== 0;
    }

    /* ************************************************************************************* */
    /* DEPRECATED METHODS ****************************************************************** */
    /* ************************************************************************************* */
    /**
     * @deprecated Dialogs should no longer be registered with ezApi but instead register via EzApi.ezInstance.ezRegisterApi()
     * @public @method
     * @param {string} dialogName
     * @param {object} dialogInstance
     * @returns {object}
     * Returns instance associated with the dialog name
     */
    ezRegisterDialog(dialogName, dialogInstance) {
        if (!EzString.hasLength(dialogName)) {
            throw new Error('[EzApi.ezRegisterDialog]: A non-empty name is required to register with ezApi.');
        }
        if (!EzObject.isValid(dialogInstance)) {
            throw new Error('[EzApi.ezRegisterDialog]: You can only register valid, non-null instances with ezApi.');
        }

        EzApi.ezInstance.ezclocker[dialogName] = dialogInstance;
        return EzApi.ezInstance.ezclocker[dialogName];
    }

    /**
     * @deprecated Call EzApi.ezInstance.ezRegisterGlobalEvent directly with a specific name for the event.
     * @public @method
     * Registers an onReady event for the owner
     * @param {string} eventOwnerName
     */
    ezRegisterReadyEvent(eventOwnerName) {
        if (!EzString.hasLength(eventOwnerName)) {
            EzApi.ezInstance.ezLogError('A valid, non-empty event owner name is required to register a global OnReady event.');
        }

        EzApi.ezInstance.ezRegisterGlobalEvent(
            eventOwnerName,
            'onReady',
            `_EzOn_${eventOwnerName}_Ready`);
    }

    /**
     * @public @method
     * Verifies the passed function is valid, and returns the call to that method. Otherwise, returns null
     * @deprecated Move to EzApi.ezInstance.ezCallback()
     */
    callBack(...args) {
        return EzApi.ezInstance.ezCallBack.apply(this, args);
    }

    /**
     * @public @method
     * Determines of the provided object reference has the property named as the string provided in propertyName.
     * @param {object} ref
     * @param {string} propertyName
     * @returns {boolean}
     */
    ezJsonObjectHasProperty(ref, propertyName) {
        if (!EzObject.isValid(ref) || !EzString.hasLength(propertyName)) {
            return false;
        }

        return Object.hasOwn(ref, propertyName);
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezReturnValidOrDefault()
     * @public @method
     * Returns the provided item for assignment to a let if the item is valid. Otherwise, the defaultItem is returned for
        assignment.
     * @param {*} item
     * @param {*} defaultItem
     */
    assignOrDefault(item, defaultItem) {
        return EzApi.ezInstance.ezReturnValidOrDefault(item, defaultItem);
    }

    /**
     * @public @method
     * Returns the provided item for assignment to a let if the item is valid. Otherwise, the defaultItem is returned for
     * assignment.
     * @param {*} item
     * @param {*} defaultItem
     */
    ezAssignOrDefault(item, defaultItem) {
        return EzObject.isValid(item)
            ? item
            : defaultItem;
    }

    /**
     * @public @method
     * Returns the provided aArray if it is a valid array. Otherwise, an empty array is returned.
     * @param {array} aArray
     * @returns {array}
     */
    ezAssignOrEmptyArray(aArray) {
        return EzArray.isArray(aArray)
            ? aArray
            : [];
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezReturnValidOrEmptyString()
     * @public @method
     * Returns the provided value if it is a valid string, otherwise EzString.EMPTY string is returned
     * @param {string} value
     */
    assignOrEmpty(value) {
        return EzApi.ezInstance.ezReturnValidOrEmptyString(value);
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezAssignOrNull()
     * @public @method
     * Returns the provided item to assign to a variable if the item is valid. Otherwise, null is returned
     * @deprecated
     * Usewindow.EzApi.ezInstance.ezReturnValidOrNull instead.
     * @param {*} item
     */
    assignOrNull(item) {
        return EzApi.ezInstance.ezAssignOrDefault(item, null);
    }
    /**
     * @deprecated Move to EzApi.ezInstance.ezReturnValidOrNull()
     * @public @method
     * Returns the provided item to assign to a variable if the item is valid. Otherwise, null is returned
     * @param {*} item
     */
    ezAssignOrNull(item) {
        return EzApi.ezInstance.ezAssignOrDefault(item, null);
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezDecode()
     * @public @method
     * Decodes the provided string, converting to a blank string first if null, undefined
     * @param {string} string
     */
    decode(stringValue) {
        return EzUrl.decodeURL(
            EzString.stringWithLengthOrEmpty(stringValue));
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezNumberToString()
     * @public @method
     * Converts a number to a string or returns NAN if not posslbe
     * @param {number} num
     */
    numberToString(num) {
        EzNumber.asString(num);
    }

    /**
     * @public @method
     * Returns the valie of the property named {propertyKey} from the provided {jsonObject} if {jsonObject} contains
     * that valid property. Otherwise, the default value is returned.
     * @param {object} jsonObject
     * @param {string} propertyKey
     * @param {defaultValue|null} defaultValue
     */
    ezGetJSONObjectPropertyValueOrDefault(jsonObject, propertyKey, defaultValue) {
        return EzObject.isValid(jsonObject) && EzString.hasLength(propertyKey) && EzObject.isValid(jsonObject[propertyKey])
            ? jsonObject[propertyKey]
            : defaultValue;
    }

    /**
     * @deprecated Use EzApi.ezInstance.ezRegisterApi(name, instance); instead
     * @public @method
     * Registers an ezclocker-api object with theezApi instance with the appropriate scope.
     * @param {string} apiName
     * Name of the API object
     * @param {object} apiInstance
     * Instance of the API object
     * @param {string|null} accessScope
     * Defaults to secure scope if none provided
     * @returns {object}
     * Returns the api instance provided
     */
    ezRegister(apiName, apiInstance, accessScope) {
        if (!EzString.hasLength(apiName)) {
            throw new Error('A non-empty name is required to register with ezApi.');
        }
        if (!EzObject.isValid(apiInstance)) {
            throw new Error('You can only register valid, non-null instances with ezApi.');
        }

        // Force API uppercase to make it non-case sensitive
        //apiName = apiName.toUpperCase();
        const scope = EzString.stringWithLengthOrDefault(
            accessScope,
            EzApi.ezInstance.EzApiRegistrationScope.SECURE);

        if (EzApi.ezInstance.EzApiRegistrationScope.WINDOW === scope) {
            EzApi.ezInstance.ezLogDebug(`[ezClocker API]: ${apiName} registered as a GLOBAL API on the window object.`);

            window[apiName] = apiInstance;

            return window[apiName];
        }

        if (EzObject.isValid(apiInstance)) {
            apiInstance.ezApiScope = accessScope;
        }
        EzApi.ezInstance.ezLogDebug(`[ezClocker API]: ${apiName} registered.`);

        EzApi.ezInstance.ezclocker[apiName] = apiInstance;

        return EzApi.ezInstance.ezclocker[apiName];
    }

    /**
     * @deprecated Use EzApi.ezInstance.ezRegisterApi(name, instance); instead
     * @public @method
     * Registers theezApi as a public api
     * @param {string} apiName
     * Name of the API object
     * @param {object} apiInstance
     * Instance of the API object
     * @returns {object}
     * Returns the api instance provided
     */
    ezRegisterPublic(apiName, apiInstance) {
        if (!EzString.hasLength(apiName)) {
            throw new Error('A non-empty name is required to register with ezApi.');
        }
        if (!EzObject.isValid(apiInstance)) {
            throw new Error('You can only register valid, non-null instances with ezApi.');
        }

        EzApi.ezInstance.ezclocker[apiName] = apiInstance;

        return EzApi.ezInstance.ezclocker[apiName];
    }

    /**
     * @deprecated Use EzApi.ezInstance.ezRegisterApi(name, instance); instead
     * @public @method
     * Registers theezApi as a secure api
     * @param {string} apiName
     * Name of the API object
     * @param {object} apiInstance
     * Instance of the API object
     * @returns {object}
     * Returns the api instance provided
     */
    ezRegisterSecure(apiName, apiInstance) {
        if (!EzString.hasLength(apiName)) {
            throw new Error('A non-empty name is required to register with ezApi.');
        }
        if (!EzObject.isValid(apiInstance)) {
            throw new Error('You can only register valid, non-null instances with ezApi.');
        }

        EzApi.ezInstance.ezclocker[apiName] = apiInstance;

        return EzApi.ezInstance.ezclocker[apiName];
    }

    /**
     * @deprecated Use EzApi.ezInstance.ezRegisterApi(name, instance); instead
     * @public @method
     * Registers an external API
     * @param {string} apiName
     * Name of the API object
     * @param {object} apiInstance
     * Instance of the API object
     * @returns {object}
     * Returns the api instance provided
     */
    ezRegisterExternal(apiName, apiInstance) {
        if (!EzString.hasLength(apiName)) {
            throw new Error('A non-empty name is required to register with ezApi.');
        }
        if (!EzObject.isValid(apiInstance)) {
            throw new Error('You can only register valid, non-null instances with ezApi.');
        }

        EzApi.ezInstance.ezclocker[apiName] = apiInstance;

        return EzApi.ezInstance.ezclocker[apiName];
    }

    /**
     * @deprecated Use EzApi.ezInstance.ezRegisterApi(name, instance); instead
     * @public @method
     * Registers theezApi as a global (root level) api
     * @param {string} apiName
     * Name of the API object
     * @param {object} apiInstance
     * Instance of the API object
     * @returns {object}
     * Returns the api instance provided
     */
    ezRegisterRoot(apiName, apiInstance) {
        if (!EzString.hasLength(apiName)) {
            throw new Error('A non-empty name is required to register with ezApi.');
        }
        if (!EzObject.isValid(apiInstance)) {
            throw new Error('You can only register valid, non-null instances with ezApi.');
        }

        EzApi.ezInstance.ezclocker[apiName] = apiInstance;

        return EzApi.ezInstance.ezclocker[apiName];
    }

    /**
     * @deprecated Use EzApi.ezInstance.ezRegisterApi(name, instance); instead
     * @public @method
     * Adds a public scoped object into theezApi with the provided name.
     * @param {string} name
     * @param {object} reference
     * @returns {object}
     * Returns the reference
     */
    addPublicApi(name, reference) {
        return EzApi.ezInstance.ezRegisterPublic(name, reference);
    }

    /**
     * @deprecated Use EzApi.ezInstance.ezRegisterApi(name, instance); instead
     * @public @method
     * Adds an ezClocker dialog reference into theezApi with the provided name
     * @param {string} name
     * @param {object} reference
     * @returns {object}
     * Returns the reference
     */
    addDialog(name, reference) {
        EzApi.ezInstance.dialogs[name] = typeof reference !== 'undefined' ? reference : null;
    }

    /**
     * @deprecated Use EzApi.ezInstance.ezRegisterApi(name, instance); instead
     * @public @method
     * Adds a secure scoped object into theezApi with the provided name
     * @param {string} name
     * @param {object} reference
     * @returns {object}
     * Returns the reference
     */
    addSecureApi(name, reference) {
        return EzApi.ezInstance.ezRegisterSecure(name, reference);
    }

    /**
     * @deprecated Use EzApi.ezInstance.ezRegisterApi(name, instance); instead
     * @public @method
     * Adds an externall scoped object into theezApi with the provided name
     * @param {string} name
     * @param {object} reference
     * @returns {object}
     * Returns the reference
     */
    addExternalApi(name, reference) {
        return EzApi.ezInstance.ezRegisterExternal(name, reference);
    }

    /**
     * @deprecated Use EzApi.ezInstance.ezApiExists(apiName) instead.
     * @public @method
     * Returns true if a reference with the provided name exists in the public scope
     * @param {string} name
     * @returns {boolean}
     */
    doesPublicApiExist(name) {
        return typeof EzApi.ezInstance.Public[name] !== 'undefined' && EzApi.ezInstance.Public[name];
    }

    /**
     * @deprecated Use EzApi.ezInstance.ezApiExists(apiName) instead.
     * @public @method
     * Returns true if a reference with the provided name exists in the secure scope
     * @param {string} name
     * @returns {boolean}
     */
    doesSecureApiExist(name) {
        return typeof EzApi.ezInstance.Secure[name] != 'undefined' && EzApi.ezInstance.Secure[name];
    }

    /**
     * @deprecated Use EzApi.ezInstance.ezApiExists(apiName) instead.
     * @public @method
     * Returns true if a reference with the provided name exists in the external scope
     * @param {string} name
     * @returns {boolean}
     */
    doesExternalApiExist(name) {
        return typeof EzApi.ezInstance.Secure[name] != 'undefined' && EzApi.ezInstance.External[name];
    }

    /**
     * @deprecated Switch to EzApi.ezInstance.ezSelf() instead
     * @public @method
     * This is the same implementation as EzApi.ezInstance.ezSelf(), which is the preferred method to use.
     * @param {string} apiName
     * @param {Object|null} selfToUse
     * @returns {object}
     */
    ezGetSelf(apiName, selfToUse) {
        return EzObject.isValid(selfToUse) ? selfToUse : EzApi.ezInstance.ezGetApi(apiName);
    }

    /**
     * @deprecated DO NOT USE
     * @public @method
     * Returns the reference for the given apiName by scanning all the possible api categories for the name and returning
     * the first encountered using the order: Public, Secure, Dialogs, External, Window
     * @param {string} apiName
     * @returns {object}
     */
    ezGetApi(apiName) {
        if (!EzString.hasLength(apiName)) {
            EzApi.ezInstance.ezLogError('EzApi.ezInstance.ezGetSelf requires a valid, non-empty api name param.');

            // a name IS required
            return null;
        }

        let theApi = EzApi.ezInstance.ezclocker[apiName];

        if (!EzObject.isValid(theApi)) {
            // attempt to pull from global
            theApi = window[apiName];
        }

        if (!EzObject.isValid(theApi)) {
            EzApi.ezInstance.ezLogError(`Unable to locate an ezApi with the name ${apiName}`);

            return null;
        }

        return theApi;
    }

    /**
     * @deprecated Move to EzJson.toJson()
     * @public @method
     * Converts an object to JSON string
     * @param {*} jsonObject
     */
    toJsonString(item) {
        return EzJson.toJson(item);
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezFromJson()
     * @public @method
     * Returns the Javascript Object created from the provided String. If a non-string value is passed, that value
     * is simply reflected back in this call.
     * @param {string} jsonString
     */
    fromJsonString(jsonString) {
        return EzJson.fromJson(jsonString);
    }

    /**
     * @deprecated Move to ezUi.ezElementExists()
     * @public @method
     * Determines if the element with provided ID exists (using $() call)
     * @param {string} id
     * @returns {boolean}
     */
    ezElementExists(id) {
        return EzApi.ezInstance.ezElementExists$(`#${id}`);
    }

    /**
     * @deprecated Move to ezUi.ezElementExists$()
     * @public @method
     * Determines if the element with provided ID exists (using $() call)
     * @param {string} jquerySelector
     * @returns {boolean}
     */
    ezElementExists$(jquerySelector) {
        if (!EzString.hasLength(jquerySelector)) {
            return false;
        }

        const element = EzApi.ezInstance.ez$(jquerySelector);

        return EzObject.isValid(element) && 0 !== element.length;
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezIsUndefined()
     * @public @method
     * Return true if the provided item is undefined
     * @param {*} item
     */
    isUndefined(item) {
        return typeof item === 'undefined';
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezGetObjectType()
     * @public @method
     * Returns the Object.prototype.toString.call() result applied to the provided object
     * @param {*} object
     */
    getObjectType(object) {
        return Object.prototype.toString.call(object);
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezIsNull()
     * @public @method
     * Returns true if the provided item is null (does not evaluate undefined)
     * @param {*} item
     */
    isNull(item) {
        return null == item;
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezIsObject()
     * @public @method
     * Returns true if the provided item is an object type
     * @param {*} item
     */
    isObject(item) {
        return EzObject.isValid(item) && typeof item === 'object';
    }

    /**
     * @deprecated Switch to EzObject.isValid()
     * @public @method
     * Returns true if the item passed is defined and not null.
     * @param {*} item
     */
    isValid(item) {
        return typeof item !== 'undefined' && item !== null;
    }

    /**
     * @deprecated Switch to !EzObject.isValid()
     * @public @method
     * Returns true if the item passed is undefined OR null
     * @param {*} item
     */
    isNotValid(item) {
        return !EzObject.isValid(item);
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezAnyNotValid()
     * @public @method
     * Determines if any of the passed arguments are not valid
     */
    anyNotValid(...args) {
        for (const argument of args) {
            if (!EzObject.isValid(argument)) {
                return true;
            }
        }

        return false;
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezIsString()
     * @public @method
     * Determines if the value passed is a string
     * @param {*} value
     */
    isString(object) {
        return EzObject.isValid(object) && typeof object === 'string';
    }

    /**
     * @public @method
     * Safely returns the index of the first occurance of aPart within the provdied aString by checking for null
     * or undefined values before hand.
     * If aString is not a valid string then -1 is returned
     * If aPart is not a valid string, then -1 is returned
     * @returns {number}
     */
    ezIndexOf(aString, aPart) {
        return undefined !== aString && null !== aString && 'string' === typeof aString &&
            undefined !== aPart && null !== aPart && 'string' === typeof aPart
            ? aString.indexOf(aPart)
            : -1;
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezIsMoment()
     * @public @method
     * Determines if the passed object is an actual moment.js object or not.
     * @param {*} object
     */
    isMoment(object) {
        return EzObject.isValid(object) && true === object._isAMomentObject;
    }

    /**
     * @deprecated Use !EzString.hasLength() instead.
     * @public @method
     * Determines if the passed string is undefined, null, or has length zero
     * @param {string} string
     */
    isEmptyString(stringValue) {
        return !EzString.hasLength(stringValue);
    }

    /**
     * @deprecated Use EzApi.ezInstance.ezIsEmptyArray() instead
     * @public @method
     * Determines if the passed array is not undefined, not null, and has a length greater than zero
     * @param {array} array
     */
    isEmptyArray(array) {
        return !EzArray.hasLenght(array);
    }

    /**
     * @deprecated Use EzApi.ezInstance.ezIsNotEmptyArray() instead.
     * @public @method
     * Determines if the passed array is not undefined, not null, and has a length greater than zero
     * @param {array} array
     */
    isNotEmptyArray(array) {
        return !EzApi.ezInstance.ezIsEmptyArray(array);
    }

    /**
     * @deprecated Use EzArray.isArray() instead.
     * @public @method *
     * Determines if the passed object is an array or not.
     * @param {array|null} array
     */
    isArray(array) {
        return EzObject.isValid(array) && EzApi.ezInstance.ezGetObjectType(array) === '[object Array]';
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezIsNumber()
     * @public @method
     * Determines if the passed object is a number type.
     * @param {*} object
     */
    isNumber(aObject) {
        return EzObject.isValid(aObject) &&
            (typeof aObject === 'number' || EzApi.ezInstance.ezGetObjectType(aObject) === '[object Number]');
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezIsBoolean()
     * @public @method
     * Determines if the passed object is a boolean type.
     * @param {*} object
     */
    isBoolean(object) {
        return EzObject.isValid(object) && EzApi.ezInstance.ezGetObjectType(object) === '[object Boolean]';
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezIsFalse()
     * @public @method
     * Returns true if the boolean value is false. Otherwise, returns false if the value is NOT a boolean at all OR
     * is actually true.
     * @param {boolean} value
     */
    isFalse(value) {
        return EzApi.ezInstance.ezIsBoolean(value)
            ? false === value
            : false;
    }

    /**
     * @deprecated Use EzFunction.isFunction() instead
     * @public @method
     * Returns true if the passed ref is a javascript function
     * @param {Function} functionRef
     */
    isFunction(functionRef) {
        return EzObject.isValid(functionRef) && 'function' === typeof functionRef;
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezIsNotEmptyString()
     * @public @method
     * Determines if the passed string is not undefined, not null, AND has a length greater than zero
     * @param {string} stringValue
     */
    isNotEmptyString(stringValue) {
        return EzApi.ezInstance.ezIsString(stringValue) && 0 < stringValue.length;
    }

    /**
     * @deprecated Move to EzString.stringOrDefault()
     * @public @method
     * Returns the passed string OR if the string is empty, null, or undefined a blank string is returned
     * @param {string} stringValue
     */
    gString(stringValue) {
        return EzString.hasLength(stringValue)
            ? stringValue
            : EzString.EMPTY;
    }

    /**
     * @public @method
     * Alternative wrapper for ezReject
     * @param {object} response
     * @deprecated
     * Migrate to: EzPromise.reject(...)
     */
    Reject(response) {
        return EzApi.ezInstance.ezReject(response);
    }

    /**
     * @deprecated Move to EzApi.ezInstance.ezResolve()
     * @public @method
     * Executes Promise.resolve, passing the provided response in the resolve.
     * @param {object} response
     */
    Resolve(response) {
        return EzApi.ezInstance.ezResolve(response);
    }

    /**
     * @public @method
     * Use in promise.catch handlers to genericlly catch issues from the promise.
     * @param {array} args
     */
    ezPromiseCatch(...args) {
        const logError = EzArray.hasLength(args)
            ? `EzApi caught an unhandled promise exception/catch. Catch arguments: ${EzJson.toJson(args)}`
            : 'EzApi caught an unhandled promise exception/catch. No arguments available.';

        window.console.error(logError);

        return EzApi.ezInstance.ezCreateErrorResponse(
            500,
            'An unexpected error occurred while executing a Promise',
            args);
    }

    /**
     * @public @method
     * Creates a basic response with error message, error code, and optional entity.
     * @param {string} em
     * @param {Number|null} ec
     * @param {string|null} optionalEndPoint
     * @param {Object|null} optionalEntity
     * @returns {object}
     */
    ezCreateErrorResponse(ec, em, optionalEndPoint, optionalEntity) {
        const eResponse = {
            errorCode: EzApi.ezInstance.ezIsNumber(ec)
                ? ec
                : 500,
            message: !EzString.hasLength(em)
                ? 'Unexpected error'
                : em
        };

        if (EzString.hasLength(optionalEndPoint)) {
            eResponse['endPoint'] = optionalEndPoint;
        }

        if (EzObject.isValid(optionalEntity)) {
            eResponse['entity'] = optionalEntity;
        }

        return eResponse;
    }

    /**
     * @public @method
     * Determines if the objectRef has the property propertyName. Returns null if the objectRef is false, the
     * @param {object} objectRef
     * @param {string} propertyName
     * @returns {boolean}
     */
    ezHasOwnProperty(objectRef, propertyName) {
        if (!EzObject.isValid(objectRef)) {
            throw new EzBadParamException(
                'objectRef',
                EzApi.ezInstance,
                EzApi.ezInstance.ezHasOwnProperty);
        }
        if (!EzObject.isValid(propertyName)) {
            throw new EzBadParamException(
                'propertyName',
                EzApi.ezInstance,
                EzApi.ezInstance.ezHasOwnProperty);
        }

        const pName = EzApi.ezInstance.ezIsString(propertyName)
            ? propertyName
            : propertyName.toString();

        if (!EzString.hasLength(pName)) {
            throw new EzBadParamException(
                'propertyName',
                EzApi.ezInstance,
                EzApi.ezInstance.ezHasOwnProperty);
        }

        return Object.hasOwn(objectRef, pName);
    }

    /**
     * @public @method
     * Returns if the objectRef is an instance of the provided clazz reference.
     * Note: The instanceof operator to test if the prototype property of a constructor appears anywhere in the
     * prototype chain of an object. The return value is a boolean value.
     * @param {object} objectRef
     * @param {Constructor|Class} clazz
     */
    ezInstanceOf(objectRef, clazz) {
        if (!EzObject.isValid(objectRef)) {
            throw new EzBadParamException(
                'objectRef',
                EzApi.ezInstance,
                EzApi.ezInstance.ezInstanceOf);
        }
        if (!EzObject.isValid(clazz)) {
            throw new EzBadParamException(
                'clazz',
                EzApi.ezInstance,
                EzApi.ezInstance.ezInstanceOf);
        }

        return objectRef instanceof clazz;
    }

    /**
     * @public @method
     * Returns if the objectRef is an instance of the provided clazz reference. In addition, a warning log is written
     * if the provided objectRef is not an instance of the provided Clazz/prototype constructor.
     * @param {*} objectRef
     * @param {*} Clazz
     * @returns {boolean}
     */
    ezWarnIfNotInstanceOf(objectRef, Clazz) {
        if (!EzApi.ezInstance.ezInstanceOf(objectRef, Clazz)) {
            EzLogger.warn(
                EzString.msg`
                    Action object is not an instance of ${Clazz.name}.
                    Action object is an instance of ${objectRef.constructor.name}.`);

            return false;
        }

        return true;
    }

    /**
     * @public @method
     * Returns the reference of the provided ezApiName, thisRef, or paramRef if it is valid and an instance of Clazz.
     * Returns the reference based on the following requirements:
     * EzApi.ezInstance.ezclocker[ezApiName] is returned if ezApiName is a valid Api name in EzApi.ezInstance.ezclocker and is an instance of
     * the provided Clazz.
     * - OR -
     * thisRef is returned if thisRef is a valid instance of the provided Clazz
     * - OR -
     * paramRef is returned if paramRef is a valid instance of the provided Clazz
     * - Otherwise -
     * Null is returned as the self reference. In addition, a warning is logged as each contender is evaluated to
     * be an instanceof Clazz and fails that evaluation.
     * @param {Constructor|Class} Clazz
     * @param {string|null} ezApiName
     * @param {Object|null} thisRef
     * @param {Object|null} paramRef
     * @returns {Object|null}
     */
    ezSelfRef(Clazz, thisRef, ezApiName, paramRef) {
        if (!EzObject.isValid(Clazz)) {
            Clazz = Object;
        }

        const refSet = [];

        if (EzObject.isValid(thisRef)) {
            refSet.push(thisRef);
        }

        if (EzString.hasLength(ezApiName)) {
            refSet.push(EzApi.ezInstance.ezclocker[ezApiName]);
        }

        if (EzObject.isValid(paramRef)) {
            refSet.push(paramRef);
        }

        for (const ref of refSet) {
            if (EzApi.ezInstance.ezWarnIfNotInstanceOf(ref, Clazz)) {
                return ref;
            }
        }

        // None of the references met the requirements
        return null;
    }

    /**
     * @public @method
     * Returns true if the aObject is an instance of Set()
     * @param {Object|null} aObject
     * @returns {boolean}
     */
    ezIsSet(aObject) {
        return EzObject.isValid(aObject) && EzApi.ezInstance.ezGetObjectType(aObject) === '[object Set]';
    }

    /**
     * @public @method
     * Returns true if the aObject is not an instance of Set()
     * @param {Object|null} aObject
     * @returns {boolean}
     */
    ezIsNotSet(aObject) {
        return !EzApi.ezInstance.ezIsSet(aObject);
    }

    /**
     * @public @method
     * Returns true if aObject is a Set() and the set is empty.
     * @param {Set} aObject
     * @returns {boolean}
     */
    ezIsEmptySet(aObject) {
        return EzApi.ezInstance.ezIsSet(aObject) && 0 === aObject.size;
    }

    /**
     * @public @method
     * Returns true if aObject is a Set() and the set is NOT empty.
     * @param {Set} aObject
     * @returns {boolean}
     */
    ezIsNotEmptySet(aObject) {
        return EzApi.ezInstance.ezIsSet(aObject) && 0 !== aObject.size();
    }

    /**
     * @public @method
     * Returns a get/set object operating from the provided parent object selfRef. Gets the value of
     * the selfRef property named propName. Sets the value of the selfRef objects property propName OR sets the
     * setDefValue if the provided setter value is not valid.
     * @param {object} self
     * @param {string} propName
     * @param {*} setDefValue
     * @returns {GetterSetter}
     */
    ezGetSet(selfRef, propName, propType, initialValue, setterDefaultValue, setterRequirements) {
        const ezGetterSetter = new EzGetterSetter(selfRef, propName, propType, initialValue, setterDefaultValue, setterRequirements);

        return () => ezGetterSetter;
    }

    /**
     * @public @method
     * Returns a getter object operating from the provided parent object selfRef. Gets the value of
     * the selfRef property named propName.
     * @param {object} self
     * @param {string} propName
     * @returns {GetterSetter}
     */
    ezGetter(selfRef, propName) {
        const ezGetter = new EzGetter(selfRef, propName);

        return () => ezGetter;
    }

    /**
     * @public @method
     * Returns a getter object operating from the provided parent object selfRef. Gets the value of
     * the selfRef property named propName.
     * @param {object} self
     * @param {string} propName
     * @param {*} initValue
     * @returns {GetterSetter}
     */
    ezGetterX(selfRef, propName, initValue) {
        const fName = `get${propName.replace(propName.charAt(0), propName.charAt(0).toUpperCase())}`;

        selfRef[fName] = () => selfRef[propName];

        return initValue;
    }

    /**
     * @public @method
     * Verifies the core dependencies are available
     * @returns {boolean}
     */
    ezValidateCoreDependencies() {
        const ezApiDep = '/public/javascript/common/ezapi.js';

        const publicJsCommon = '/public/javascript/common';

        if (!EzObject.isValid(ezLogger)) {
            EzLogger.error(
                `Definition of ${publicJsCommon}/ezclocker-logger.js is required after ${ezApiDep}`);

            return false;
        }
        if (!EzObject.isValid(EzApi.ezInstance.ezclocker.ezEvents)) {
            EzLogger.error(
                `Definition of ${publicJsCommon}/ez-events.js is required after ${ezApiDep}`);

            return false;
        }
        if (!EzObject.isValid(EzApi.ezInstance.ezclocker.ezUrlHelper)) {
            EzLogger.error(
                `Definition of ${publicJsCommon}/ezclocker-url-helper2.js is required after ${ezApiDep}`);

            return false;
        }
        if (!EzObject.isValid(EzApi.ezInstance.ezclocker.nav)) {
            EzLogger.error(
                `Definition of ${publicJsCommon}/ezclocker-navigation-helper.js is required after ${ezApiDep}`);

            return false;
        }
        if (!EzObject.isValid(EzApi.ezInstance.ezclocker.ezDateTime)) {
            EzLogger.error(
                `Definition of ${publicJsCommon}/ez-date-time.js is required after ${ezApiDep}`);

            return false;
        }
        if (!EzObject.isValid(EzApi.ezInstance.ezclocker.ezBrowserInfo)) {
            EzLogger.error(
                `Definition of ${publicJsCommon}/ezclocker-mobile-helper.js is required after ${ezApiDep}`);

            return false;
        }

        return true;
    }

    /**
     * @public @method
     * Makes a deep copy of the propertyes of the provided sourceObject. Methods will not be copied over to the new Object.
     * @param {object} sourceObject
     * @returns {object}
     */
    ezPropClone(sourceObject) {
        if (!EzObject.isValid(sourceObject)) {
            return sourceObject;
        }

        return JSON.parse(JSON.stringify(sourceObject));
    }

    /**
     * @public @method
     * Makes a deep clone of the sourceObject.
     * Handles the cloning of functions and multiple/cyclic references (what this means is that if two properties in the
     * tree which is cloned are references of the same object, the cloned object tree will have these properties point to
     * one and the same clone of the referenced object)
     * Also solves for cyclic dependencies to prevent an infinite loop.
     * The complexity of the algorithm is O(n)
     * Research Link
     * https://stackoverflow.com/questions/728360/how-do-i-correctly-clone-a-javascript-object
     * @param {object} sourceObject
     * @returns {object}
     */
    ezDeepClone(sourceObject) {
        // used to remove the unique ids when finished
        const originalObjectsArray = [];

        let next_objid = 0;

        function objectId(aObject) {
            if (aObject == null) {
                return null;
            }

            if (!EzObject.isValid(aObject.__obj_id)) {
                aObject.__obj_id = next_objid;

                next_objid++;

                originalObjectsArray[aObject.__obj_id] = aObject;
            }

            return sourceObject.__obj_id;
        }

        const clonedObjectsArray = [];

        const cloneRecursive = (aObject, ...args) => {
            if (null === aObject) {
                return aObject;
            }

            const typeOfObject = typeof aObject;

            if ('string' === typeOfObject || 'number' === typeOfObject || 'boolean' === typeOfObject) {
                return aObject;
            }

            // Handle Date
            if (aObject instanceof Date) {
                const dateCopy = new Date();

                dateCopy.setTime(aObject.getTime());

                return dateCopy;
            }

            // Handle Array
            if (aObject instanceof Array) {
                const arrayCopy = [];

                for (let x = 0; x < aObject.length; ++x) {
                    arrayCopy[x] = cloneRecursive(aObject[x]);
                }

                return arrayCopy;
            }

            // Handle Object
            if (aObject instanceof Object) {
                if (undefined !== clonedObjectsArray[objectId(aObject)]) {
                    return clonedObjectsArray[objectId(aObject)];
                }

                const objectCopy = aObject instanceof Function
                    // Handle Function
                    ? aObject.apply(this, args)
                    : {};

                clonedObjectsArray[objectId(aObject)] = objectCopy;

                for (const propName in aObject) {
                    if ('__obj_id' !== propName && EzObject.hasProperty(aObject, propName)) {
                        objectCopy[propName] = cloneRecursive(aObject[propName]);
                    }
                }

                return objectCopy;
            }

            throw new Error(
                EzString.em`
                    Unable to clone an object of type ${sourceObject.constructor.name}.
                    The type is not yet supported.`);
        };

        // Remove the unique ids
        for (const originalObject of originalObjectsArray) {
            delete originalObject.__obj_id;
        }

        return cloneRecursive(sourceObject);
    }

    /**
     * @public @method
     * Replaces all characters in the provided value with the obscureChar (uses * as default obscure char)
     * If value is empty or null then {obscureChar} is returned.
     * If obscureChar is empty or null, then * is used.
     * @param {string|null} value
     * @param {string|null} obscureChar
     * @returns {string}
     */
    ezObscureValue(value, obscureChar = '*') {
        const oChar = EzString.hasLength(obscureChar)
            ? obscureChar
            : '*';

        if (!EzString.hasLength(value)) {
            return oChar;
        }

        return value.replace(/(.?)/g, oChar);
    }

    /**
     * Performs a shallow copy of the provided sourceObject. Will include any 'shallow' methods on the sourceObject.
     * @param {object} sourceObject
     * @returns {object}
     */
    ezShallowClone(sourceObject) {
        if (!EzObject.isValid(sourceObject)) {
            return sourceObject;
        }

        return { ...sourceObject };
    }

    /**
     * @public @method
     * Generates a hash code for the provided value. If the value is not a string, the toString() value is used
     * to calculate the hash.
     * @returns {number}
     */
    ezHash32(value) {
        if (null === value || undefined === value) {
            throw new EzBadParamException('value', 'EzApi', 'ezGenerateHashCode');
        }

        return value
            .split(EzString.EMPTY)
            .reduce(
                (prevHash, currVal) => {
                    return (((prevHash << 5) - prevHash) + currVal.charCodeAt(0)) | 0;
                });
    }

    ezValidContextDataRequiredException(dataName, functionName) {
        return EzApi.ezInstance.ezException(`A ${dataName} is required for EzClockerContext. ${functionName}`);
    }


    /**
     * @public @method
     * Attempts to separate the provided fullName into firstName and lastName parts.
     * @returns {object}
     * {
     *     firstName: <first_name_part>,
     *     lastName: <last_name_part>
     * };
     */
    ezFullNameToFirstLastName(fullName, lastNameIsFirst) {
        const result = {
            firstName: EzString.EMPTY,
            lastName: EzString.EMPTY
        };

        if (!EzString.hasLength(fullName)) {
            return result;
        }

        let nameParts;

        if (EzBoolean.isTrue(lastNameIsFirst)) {
            // Assuming fullName is formatted as: 'LastName, FirstName' OR 'LastName FirstName' OR 'LastName'
            nameParts = fullName.split(',');

            if (1 === nameParts.length) {
                nameParts = fullName.split(' ');
            }

            if (1 === nameParts.length) {
                result.lastName = fullName.trim();

                return result;
            }

            for (let index = 0; index < nameParts.length; index++) {
                if (0 === index) {
                    result.lastName = nameParts[index].trim();
                } else {
                    result.firstName += `${nameParts[index]} `;
                }
            }

            result.firstName = result.firstName.trim();

            return result;
        }

        // Assuming fullName is formatted as: 'FirstName LastName' OR 'FirstName'
        nameParts = fullName.split(' ');

        if (1 === nameParts.length) {
            result.lastName = fullName.trim();
            return result;
        }

        for (let index = 0; index < nameParts.length; index++) {
            if (0 === index) {
                result.firstName = nameParts[index].trim();
            } else {
                result.lastName += `${nameParts[index]} `;
            }
        }

        result.lastName = result.lastName.trim();

        return result;
    }

    /**
     * @deprecated
     * Move all use of this method. Will get removed in a future version.
     * @public @method
     * @param {string} ezTypeName
     * @param {object} thisRef
     */
    ezSelfFromThis(ezTypeName, thisRef) {
        if (!EzString.hasLength(ezTypeName)) {
            throw new EzBadParamException('ezTypeName', 'EzApi', 'ezSelfFromThis');
        }
        if (!EzObject.isValid(thisRef)) {
            throw new EzBadParamException('thisRef', 'EzApi', 'ezSelfFromThis');
        }

        if (EzObject.hasProperty(thisRef, 'ezTypeName') && ezTypeName === thisRef.ezTypeName ||
            ezTypeName === thisRef.constructor.name) {
            // verified type
            return thisRef;
        }

        // Unable to verify type, print warning
        EzLogger.warn(`Unable to verify the provided thisRef is of type ${ezTypeName}`);

        return thisRef;
    }

    /**
     * @public @method
     * Returns all the names of the parameters of the provided function ref (if any)
     * @returns {array}
     */
    ezGetFunctionParamNames(functionRef) {
        if (!EzFunction.isFunction(functionRef)) {
            throw new EzBadParamException('functionRef', 'EzApi', 'ezGetFunctionParamNames');
        }

        const regEx = /\(([\s\S]*?)\)/;

        const functionParamInfo = regEx.exec(functionRef);

        if (!EzObject.isValid(functionParamInfo)) {
            return [];
        }

        return functionParamInfo[1]
            .split(',')
            .filter(
                (paramName) => 'string' === typeof paramName && 0 < paramName.length)
            .reduce(
                (accumulator, currentValue) => {
                    const reducedArray = 'string' === typeof accumulator
                        ? [accumulator.trim()]
                        : accumulator;

                    reducedArray.push(currentValue.trim());

                    return reducedArray;
                });
    }

    /**
     * @public @method
     * Determines if the singleton instance associated with the provided ezApiName is ready and registered with ezApi.]
     * @param {undefined|null|string} ezApiName
     * Defeualt: EzString.EMPTY
     * @returns {boolean}
     */
    ezIsApiReady(ezApiName = EzString.EMPTY) {
        return EzString.hasLength(ezApiName) && EzApi.ezInstance?.ready && EzApi.ezInstance?.ezclocker &&
            EzApi.ezInstance.ezclocker?.[ezApiName]?.ready;
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @public @method @property
     * Stores EzApi's singleton instance's current states.
     * Default: []
     *
     * @type {array}
     * An array of valid enumeration property value from  EzInstanceState
     */
    #ezInstanceStates = [];
    /**
     * @public @property @getter
     * Gets EzApi's singleton instance's current states.
     *
     * @returns {array}
     * An array of valid enumeration property value from  EzInstanceState
     */
    get ezStates() {
        return EzArray.arrayOrEmpty(this.#ezInstanceStates);
    }
    /**
     * @public @property @setter
     * Sets EzApi's singleton instance's current states.
     *
     * @param {array} ezInstanceStates
     * Default: []
     * An array of valid enumeration property values from EzInstanceState
     */
    set ezStates(ezInstanceStates = []) {
        this.#ezInstanceStates = EzArray.arrayOrEmpty(ezInstanceStates);
    }

    /**
         * @private @field
         * Stores if EzApi's singleton global instance is ready to use
         * Defualt: false
         *
         * @type {boolean}
         */
    #ready = false;
    /**
     * @public @property @getter
     * Gets if this instance of ezApi is ready to be used
     * @returns {boolean}
     */
    get ready() {
        return this.#ready;
    }
    /**
     * @public @property @setter
     * Sets if this instance of ezApi is ready to be used
     * @param {boolean} readyBoolean
     */
    set ready(readyBoolean) {
        this.#ready = EzBoolean.booleanOrFalse(readyBoolean);
    }

    /**
     * @static
     * @public @readonly @property
     * Global name of EzApi instance reference
     *
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezApi';
    }

    /**
     * @static
     * @public @readonly @property
     * A map of id t0 event name of document events triggered by EzApi
     *
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzApi_Ready',
            onEzApiReady: 'onEzApiReady'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the singleton instance for EzApi
     * Default: globalThis?.ezApi || null
     *
     * @type {EzApi}
     */
    static #ezInstance = globalThis?.ezApi
        ? globalThis.ezApi
        : null;
    /**
     * @static
     * @public @method @field
     * Gets the singleton instance of EzApi
     *
     * @returns {null|EzApi}
     */
    static get ezInstance() {
        return this.#ezInstance;
    }

    /**
     * @static
     * @private @property
     * Stores the initialization state of EzApi's singleton instance
     * Default if globalThis?.ezApi is EzRegistrationState.REGISTERED
     * Default if globalThis?.ezApi is false is EzRegistrationState.NOT_REGISTERED
     *
     * @type {string}
     * A valid enumeration property value from EzRegistrationState
     */
    static #ezInitializationState = globalThis?.ezApi
        ? EzRegistrationState.REGISTERED
        : EzRegistrationState.NOT_REGISTERED;
    /**
     * @static
     * @public @method @field
     * Gets the singleton instance of EzApi
     *
     * @returns {null|EzApi}
     */
    static get ezInitializationState() {
        return this.#ezInitializationState;
    }

    /**
     * @static
     * @private @field
     * Stores EzApi's onEzApiReadyCustomEvent CustomEvent instance.
     *
     * @type {CustomEvent}
     */
    static #onEzApiReadyCustomEvent = null;
    /**
     * @static
     * @public @readonly @property
     * Gets EzApi's onEzApiReadyCustomEvent CustomEvent instance.
     *
     * @returns {CustomEvent}
     */
    static get onEzApiReadyCustomEvent() {
        if (null == this.#onEzApiReadyCustomEvent) {
            this.#onEzApiReadyCustomEvent = new CustomEvent(
                this.ezEventNames.onEzApiReady,
                {
                    bubbles: true,
                    composed: true,
                    ezApi: this.ezInstance
                });
        }

        return this.#onEzApiReadyCustomEvent;
    }

    /**
     * @static
     * @private @field
     * Stores EzApi's onReadyCustomEvent CustomEvent instance.
     *
     * @type {CustomEvent}
     */
    static #onReadyCustomEvent = null;
    /**
     * @static
     * @public @readonly @property
     * Gets EzApi's onReadyCustomEvent CustomEvent instance.
     *
     * @returns {CustomEvent}
     */
    static get onReadyCustomEvent() {
        if (null == this.#onReadyCustomEvent) {
            this.#onReadyCustomEvent = new CustomEvent(
                this.ezEventNames.onReady,
                {
                    bubbles: true,
                    composed: true,
                    ezApi: this.ezInstance
                });
        }

        return this.#onReadyCustomEvent;
    }

    /**
     * @public @static
     * Initializes the EzApi class assigning ezApi as the global reference.
     * EzApi.ezInitialize should only get called during initially loading of this script.
     *
     * @returns {undefined|null|EzApi}
     */
    static ezCreateSingletonInstance() {
        const canInit = EzBoolean.booleanOrFalse(
            'interactive' === document?.readyState &&
            EzRegistrationState.PENDING === EzApi?.ezInitializationState &&
            !EzArray.hasLength(EzApi?.ezInstance?.ezStates));

        if (!EzObject.isValid(EzApi?.ezInitializationState) || EzApi.ezInitializationState === EzRegistrationState.NOT_REGISTERED) {
            EzApi.#ezInitializationState = EzRegistrationState.PENDING;

            document.addEventListener(
                EzApi.DOCUMENT_EVENT_NAME_onreadystatechange,
                EzApi.ezCreateSingletonInstance);

            document.addEventListener(
                EzApi.DOCUMENT_EVENT_NAME_DOMContentLoaded,
                EzApi.ezCreateSingletonInstance);
        } else if (canInit && !EzObject.isValid(EzApi.ezInstance)) {
            // Document ready, create the singleton global instance of EzApi

            EzApi.#ezInstance = globalThis?.ezApi
                ? globalThis.ezApi
                : new EzApi();

            globalThis[EzApi.ezApiName] = EzApi.ezInstance;

            EzApi.ezInstance.ezInit();

            EzApi.#ezInitializationState = EzRegistrationState.INITIALIZED;

            // Fire the EzApi Ready events
            document.dispatchEvent(EzApi.onEzApiReadyCustomEvent);

            document.dispatchEvent(EzApi.onReadyCustomEvent);

            document.removeEventListener(
                EzApi.DOCUMENT_EVENT_NAME_onreadystatechange,
                EzApi.ezCreateSingletonInstance);

            document.removeEventListener(
                EzApi.DOCUMENT_EVENT_NAME_DOMContentLoaded,
                EzApi.ezCreateSingletonInstance);
        }

        return EzApi.ezInstance;
    }

    /**
     * @public @method @constructor
     * Creates a new instance of EzApi
     */
    constructor() {
        this.ezStates.push('CONSTRUCTED');

        this.ezclocker['EzApiRegistrationScope'] = this.EzApiRegistrationScope;
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}

globalThis.EzApi = EzApi;

globalThis.ezApi = globalThis?.[EzApi.ezApiName] || EzApi.ezCreateSingletonInstance();

/**
 * Exporting the singleton reference
 */
window.ezApi = globalThis.ezApi;

